import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  X,
  RotateCcw,
  AlertTriangle,
  History,
  Video,
  ArrowRight,
  Layers,
  Clock,
  RefreshCw,
  Edit2,
  Check,
  Plus,
  Flag,
  HardDrive,
  Download,
  CheckCircle2,
} from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { sportsService } from './sportsService'
import { fieldCaptureService } from './fieldCaptureService'
import { offlineIndexedDb } from '../../lib/offline/indexedDb'
import { syncManager } from '../../lib/offline/syncManager'
import { tabCoordinator } from '../../lib/offline/tabLock'
import { ConsoleSyncStatus, DraftState } from '../../types/offline'
import { SyncOperationsModal } from './SyncOperationsModal'
import {
  TeamBlockCapture,
  FieldSnap,
  SnapParticipant,
  SnapEvent,
  MatchContext,
} from '../../types/fieldCapture'
import {
  LineupSlot,
  PrimaryResult,
  RosterAthleteOption,
  OffensivePlay,
  DefensiveCall,
  LineupTemplate,
  PracticeScript,
  SecondaryEventType,
} from '../../types/sports'
import { QuickSubstitutionModal } from './QuickSubstitutionModal'
import { VideoReferenceModal } from './VideoReferenceModal'
import { PastSnapDetailModal } from './PastSnapDetailModal'
import { SnapHistoryModal } from './SnapHistoryModal'
import {
  formatFieldYard,
  formatNetYards,
  calculateNetYards,
} from './fieldYardageUtils'

interface FieldConsoleViewProps {
  captureId: string
  onClose: () => void
}

interface SelectedActor {
  athleteId: string
  name: string
  dorsal: number | null
  unit: 'offense' | 'defense'
  position: string
}

const ACTION_DEFINITIONS: {
  id: SecondaryEventType | 'penalty'
  label: string
  unit: 'offense' | 'defense' | 'both'
  badgeColor: string
}[] = [
  // Defensa
  { id: 'flag_attempt_success', label: 'Flag Conseguido', unit: 'defense', badgeColor: 'var(--status-success-bg)' },
  { id: 'flag_attempt_miss', label: 'Flag Fallado', unit: 'defense', badgeColor: 'var(--brand-yellow-subtle)' },
  { id: 'pressure', label: 'Presión / Hurry', unit: 'defense', badgeColor: 'var(--surface-subtle)' },
  { id: 'sack', label: 'Sack', unit: 'defense', badgeColor: 'var(--brand-red-subtle)' },
  { id: 'pass_defended', label: 'Pase Defendido', unit: 'defense', badgeColor: 'var(--surface-subtle)' },
  { id: 'interception', label: 'Intercepción', unit: 'defense', badgeColor: 'var(--brand-red-subtle)' },

  // Ataque
  { id: 'touchdown', label: 'Touchdown (+6)', unit: 'offense', badgeColor: 'var(--brand-red)' },
  { id: 'catch', label: 'Recepción', unit: 'offense', badgeColor: 'var(--status-success-bg)' },
  { id: 'drop', label: 'Drop (Pase caído)', unit: 'offense', badgeColor: 'var(--brand-red-subtle)' },
  { id: 'positive_execution', label: 'Gran Ejecución', unit: 'offense', badgeColor: 'var(--status-success-bg)' },

  // Común
  { id: 'penalty', label: 'Falta / Penalización', unit: 'both', badgeColor: 'var(--brand-yellow-subtle)' },
]

export const FieldConsoleView: React.FC<FieldConsoleViewProps> = ({ captureId, onClose }) => {
  const { user } = useAuth()
  const teamId =
    user?.memberships.find((m) => m.team_id)?.team_id || '33333333-3333-3333-3333-333333333333'

  // Identificador de dispositivo local para coordinación
  const deviceIdRef = useRef<string>(
    localStorage.getItem('team_spain_device_id') ||
      (() => {
        const id = 'dev-' + crypto.randomUUID().slice(0, 8)
        localStorage.setItem('team_spain_device_id', id)
        return id
      })()
  )

  // Estado general de la sesión
  const [capture, setCapture] = useState<TeamBlockCapture | null>(null)
  const [pastSnaps, setPastSnaps] = useState<FieldSnap[]>([])
  const [loading, setLoading] = useState(true)
  const [syncStatus, setSyncStatus] = useState<ConsoleSyncStatus>('synced')
  const [pendingOpsCount, setPendingOpsCount] = useState<number>(0)
  const [syncModalOpen, setSyncModalOpen] = useState<boolean>(false)
  const [isPrepared, setIsPrepared] = useState<boolean>(false)
  const [preparedAt, setPreparedAt] = useState<string | null>(null)
  const [isPreparing, setIsPreparing] = useState<boolean>(false)
  const [prepareMsg, setPrepareMsg] = useState<string | null>(null)
  const [multiTabConflict, setMultiTabConflict] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Catálogos
  const [rosterAthletes, setRosterAthletes] = useState<RosterAthleteOption[]>([])
  const [offensivePlays, setOffensivePlays] = useState<OffensivePlay[]>([])
  const [defensiveCalls, setDefensiveCalls] = useState<DefensiveCall[]>([])
  const [lineupTemplates, setLineupTemplates] = useState<LineupTemplate[]>([])
  const [scriptData, setScriptData] = useState<PracticeScript | null>(null)

  // Vista filtrada de unidad: 'both' | 'offense' | 'defense'
  const [unitView, setUnitView] = useState<'both' | 'offense' | 'defense'>('both')

  // Estado del snap actual (borrador en progreso)
  const [snapSequence, setSnapSequence] = useState<number>(1)
  const [snapStartedAt, setSnapStartedAt] = useState<string>(new Date().toISOString())
  const [hasStartedExplicitly, setHasStartedExplicitly] = useState(false)
  const [repetitionOfSnapId, setRepetitionOfSnapId] = useState<string | null>(null)

  // Llamadas del snap actual
  const [offenseCallActual, setOffenseCallActual] = useState<string>('')
  const [defenseCallActual, setDefenseCallActual] = useState<string>('')
  const [offenseCallPlanned, setOffenseCallPlanned] = useState<string>('')
  const [defenseCallPlanned, setDefenseCallPlanned] = useState<string>('')

  // Quintetos: Memoria persistente vs Snap actual (Sustitución "Solo este snap")
  const [persistentOffenseSlots, setPersistentOffenseSlots] = useState<LineupSlot[]>([])
  const [activeOffenseSlots, setActiveOffenseSlots] = useState<LineupSlot[]>([])
  const [persistentDefenseSlots, setPersistentDefenseSlots] = useState<LineupSlot[]>([])
  const [activeDefenseSlots, setActiveDefenseSlots] = useState<LineupSlot[]>([])
  const [offenseLineupName, setOffenseLineupName] = useState<string>('Ataque 1')
  const [defenseLineupName, setDefenseLineupName] = useState<string>('Defensa A')
  const [isOffenseCustom, setIsOffenseCustom] = useState(false)
  const [isDefenseCustom, setIsDefenseCustom] = useState(false)

  // Resultado y actores clave
  const [primaryResult, setPrimaryResult] = useState<PrimaryResult | null>(null)
  const [passerAthleteId, setPasserAthleteId] = useState<string | null>(null)
  const [receiverAthleteId, setReceiverAthleteId] = useState<string | null>(null)
  const [tacklerAthleteId, setTacklerAthleteId] = useState<string | null>(null)

  // EVENTOS MÚLTIPLES POR SNAP (Requerimiento 1)
  const [explicitEvents, setExplicitEvents] = useState<SnapEvent[]>([])
  const [selectedActor, setSelectedActor] = useState<SelectedActor | null>(null)

  // DOWN, POSICIÓN DE CAMPO Y YARDAS (Requerimiento 3)
  const [currentDown, setCurrentDown] = useState<number>(1)
  const [yardLineStart, setYardLineStart] = useState<number>(5)
  const [yardLineEnd, setYardLineEnd] = useState<number | null>(null)
  const [isFirstDown, setIsFirstDown] = useState<boolean>(false)
  const [isTurnover, setIsTurnover] = useState<boolean>(false)
  const [repeatScenario, setRepeatScenario] = useState<boolean>(false) // En práctica guiada

  // MARCADOR (Requerimiento 4)
  const [showSeriesScore, setShowSeriesScore] = useState<boolean>(false)
  const [seriesScoreOffense, setSeriesScoreOffense] = useState<number>(0)
  const [seriesScoreDefense, setSeriesScoreDefense] = useState<number>(0)

  // Notas tácticas y marcas de vídeo
  const [snapNotes, setSnapNotes] = useState<string>('')
  const [flaggedForReview, setFlaggedForReview] = useState(false)
  const [videoSegmentId, setVideoSegmentId] = useState<string | null>(null)
  const [videoTimestampMark, setVideoTimestampMark] = useState<string | null>(null)
  const [videoOffsetSeconds, setVideoOffsetSeconds] = useState<number>(0)

  // Control de persistencia y prevención de carrera para borrador local (400ms debounce)
  const [draftSaveStatus, setDraftSaveStatus] = useState<'pristine' | 'saving' | 'saved'>('pristine')
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isCommittingRef = useRef<boolean>(false)
  const lastCommittedSeqRef = useRef<number>(0)

  // Modales secundarios
  const [substitutionModal, setSubstitutionModal] = useState<{
    isOpen: boolean
    unit: 'offense' | 'defense'
    slotPosition: string
    currentSlot: LineupSlot | null
  }>({
    isOpen: false,
    unit: 'offense',
    slotPosition: '',
    currentSlot: null,
  })

  // TOUCHDOWN Y ATRIBUCIÓN (Ajuste M4 - Requerimiento 1)
  const [isTouchdown, setIsTouchdown] = useState<boolean>(false)
  const [scorerAthleteId, setScorerAthleteId] = useState<string | null>(null)
  const [hadLateral, setHadLateral] = useState<boolean>(false)
  const [conversionType, setConversionType] = useState<'1_point' | '2_points' | 'none' | null>(null)
  const [isConversion, setIsConversion] = useState<boolean>(false)
  const [conversionPointsAttempted, setConversionPointsAttempted] = useState<1 | 2 | null>(null)
  const [conversionResult, setConversionResult] = useState<'successful' | 'failed' | 'pending' | null>(null)
  const [precedingTouchdownSnapId, setPrecedingTouchdownSnapId] = useState<string | null>(null)
  const lastTouchdownSnapIdRef = useRef<string | null>(null)
  const [postTdModalOpen, setPostTdModalOpen] = useState<boolean>(false)

  // FINALIZACIÓN Y REAPERTURA AUDITADA (Ajuste M4 - Requerimiento 2)
  const [finalizeModalOpen, setFinalizeModalOpen] = useState<boolean>(false)
  const [finalizeNotes, setFinalizeNotes] = useState<string>('')
  const [reopenModalOpen, setReopenModalOpen] = useState<boolean>(false)
  const [reopenReason, setReopenReason] = useState<string>('')

  // HISTORIAL SNAP A SNAP COMPLETO (Ajuste M4 - Requerimiento 3)
  const [historyModalOpen, setHistoryModalOpen] = useState<boolean>(false)
  const [pastSnapDetailOpen, setPastSnapDetailOpen] = useState<boolean>(false)
  const [selectedPastSnap, setSelectedPastSnap] = useState<FieldSnap | null>(null)
  const [videoModalOpen, setVideoModalOpen] = useState(false)
  const [exitPromptOpen, setExitPromptOpen] = useState(false)

  // Cronómetro del bloque
  const [elapsedBlockSeconds, setElapsedBlockSeconds] = useState<number>(0)

  // Coordinación multi-pestaña para evitar envíos duplicados
  useEffect(() => {
    tabCoordinator.initialize(captureId, deviceIdRef.current)
    const unsub = tabCoordinator.onConflict(() => {
      setMultiTabConflict(true)
    })
    return () => {
      unsub()
      tabCoordinator.destroy()
    }
  }, [captureId])

  // Protección ante abandono con escritura local activa en curso
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (syncStatus === 'saving_local') {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [syncStatus])

  // Actualizar conteo de operaciones pendientes en outbox
  const refreshPendingCount = useCallback(async () => {
    try {
      const count = await offlineIndexedDb.countPendingOperations(captureId, teamId, user?.id)
      setPendingOpsCount(count)
      if (count === 0 && syncStatus !== 'saving_local' && syncStatus !== 'syncing') {
        setSyncStatus('synced')
      } else if (count > 0 && syncStatus !== 'saving_local' && syncStatus !== 'syncing' && syncStatus !== 'needs_attention') {
        setSyncStatus('saved_local_pending_sync')
      }
    } catch {
      // IndexedDB temporalmente no disponible
    }
  }, [captureId, teamId, user?.id, syncStatus])

  // Suscripción a eventos de SyncManager
  useEffect(() => {
    const unsub = syncManager.onStatusChange((event) => {
      if (event.type === 'sync_started' || event.type === 'sync_progress') {
        setSyncStatus('syncing')
      } else if (event.type === 'sync_completed') {
        refreshPendingCount()
      } else if (event.type === 'sync_error' || event.type === 'conflict_detected') {
        setSyncStatus('needs_attention')
      }
      refreshPendingCount()
    })

    const handleOnline = () => {
      syncManager.triggerSync('online_event', teamId, user?.id)
    }
    window.addEventListener('online', handleOnline)

    return () => {
      unsub()
      window.removeEventListener('online', handleOnline)
    }
  }, [refreshPendingCount, teamId, user?.id])

  // 1. Cargar datos iniciales unificados (online Supabase o fallback IndexedDB offline)
  const loadCaptureSession = useCallback(async () => {
    setLoading(true)
    setErrorMsg(null)
    try {
      // 1. Verificar si la captura ya fue preparada offline
      const prepPkg = await offlineIndexedDb.getPreparedPackage(captureId)
      if (prepPkg) {
        setIsPrepared(true)
        setPreparedAt(prepPkg.prepared_at)
      }

      // 2. Carga unificada de captura y snaps
      const details = await fieldCaptureService.getUnifiedCaptureDetails({
        captureId,
        teamId,
        userId: user?.id || 'anonymous',
      })

      const cap = details.capture
      setCapture(cap)
      setPastSnaps(details.snaps)

      // 3. Cargar catálogos (online con fallback a paquete preparado)
      let athletes: RosterAthleteOption[] = []
      let plays: OffensivePlay[] = []
      let calls: DefensiveCall[] = []
      let tmpls: LineupTemplate[] = []

      try {
        const [a, p, c, t] = await Promise.all([
          sportsService.getRosterAthleteOptions(teamId),
          sportsService.getOffensivePlays(teamId),
          sportsService.getDefensiveCalls(teamId),
          sportsService.getLineupTemplates(teamId),
        ])
        athletes = a
        plays = p
        calls = c
        tmpls = t
      } catch {
        if (prepPkg) {
          athletes = prepPkg.athletes || []
          plays = prepPkg.offensive_plays || []
          calls = prepPkg.defensive_calls || []
          tmpls = prepPkg.lineup_templates || []
        }
      }

      setRosterAthletes(athletes)
      setOffensivePlays(plays)
      setDefensiveCalls(calls)
      setLineupTemplates(tmpls)

      // Inicializar quintetos activos y persistentes
      const offSlots = cap.offense_slots || []
      const defSlots = cap.defense_slots || []
      setPersistentOffenseSlots(offSlots)
      setActiveOffenseSlots(offSlots)
      setPersistentDefenseSlots(defSlots)
      setActiveDefenseSlots(defSlots)

      setOffenseLineupName(cap.offense_lineup_name || 'Ataque')
      setDefenseLineupName(cap.defense_lineup_name || 'Defensa')
      setIsOffenseCustom(cap.offense_lineup_is_custom || false)
      setIsDefenseCustom(cap.defense_lineup_is_custom || false)

      // Inicializar Down y Posición de Campo desde capture
      setCurrentDown(cap.current_down || 1)
      setYardLineStart(cap.current_yard_line !== undefined && cap.current_yard_line !== null ? cap.current_yard_line : 5)
      setYardLineEnd(null)

      // Marcador de serie en team competitivo
      setShowSeriesScore(Boolean(cap.show_series_score || cap.mode === 'competitive_team'))
      setSeriesScoreOffense(cap.series_score_offense || 0)
      setSeriesScoreDefense(cap.series_score_defense || 0)

      // Preseleccionar pasador por defecto al slot QB
      const qbSlot = offSlots.find((s: LineupSlot) => s.position.toUpperCase() === 'QB')
      if (qbSlot) {
        setPasserAthleteId(qbSlot.athlete_id)
      }

      // Si tiene guion vinculado, cargarlo para asociar repeticiones previstas
      if (cap.script_id) {
        let script = prepPkg?.script || null
        if (!script) {
          try {
            const scripts = await sportsService.getPracticeScripts(teamId)
            script = scripts.find((s) => s.id === cap.script_id) || null
          } catch {
            // offline
          }
        }
        if (script) {
          setScriptData(script)
          if (script.reps && script.reps.length > cap.current_rep_index) {
            const currentRep = script.reps[cap.current_rep_index]
            setOffenseCallPlanned(currentRep.offense_play_name || '')
            setDefenseCallPlanned(currentRep.defense_call_name || '')
            setOffenseCallActual(currentRep.offense_play_name || '')
            setDefenseCallActual(currentRep.defense_call_name || '')
          }
        }
      }

      setSnapSequence(cap.current_snap_sequence || 1)
      setSnapStartedAt(new Date().toISOString())
      setHasStartedExplicitly(false)

      // Recuperar borrador local duradero si existe
      const savedDraft = await offlineIndexedDb.getDraftState(captureId)
      if (savedDraft) {
        if (savedDraft.active_offense_slots?.length === 5) setActiveOffenseSlots(savedDraft.active_offense_slots)
        if (savedDraft.active_defense_slots?.length === 5) setActiveDefenseSlots(savedDraft.active_defense_slots)
        if (savedDraft.primary_result) setPrimaryResult(savedDraft.primary_result)
        if (savedDraft.passer_athlete_id) setPasserAthleteId(savedDraft.passer_athlete_id)
        if (savedDraft.receiver_athlete_id) setReceiverAthleteId(savedDraft.receiver_athlete_id)
        if (savedDraft.tackler_athlete_id) setTacklerAthleteId(savedDraft.tackler_athlete_id)
        if (savedDraft.explicit_events) setExplicitEvents(savedDraft.explicit_events)
        if (savedDraft.current_down) setCurrentDown(savedDraft.current_down)
        if (savedDraft.yard_line_start !== undefined) setYardLineStart(savedDraft.yard_line_start)
        if (savedDraft.yard_line_end !== undefined) setYardLineEnd(savedDraft.yard_line_end)
        if (savedDraft.is_first_down !== undefined) setIsFirstDown(savedDraft.is_first_down)
        if (savedDraft.is_turnover !== undefined) setIsTurnover(savedDraft.is_turnover)
        if (savedDraft.is_touchdown !== undefined) setIsTouchdown(savedDraft.is_touchdown)
        if (savedDraft.scorer_athlete_id) setScorerAthleteId(savedDraft.scorer_athlete_id)
        if (savedDraft.had_lateral !== undefined) setHadLateral(savedDraft.had_lateral)
        if (savedDraft.conversion_type) setConversionType(savedDraft.conversion_type as '1_point' | '2_points' | 'none')
        if (savedDraft.is_conversion !== undefined) setIsConversion(Boolean(savedDraft.is_conversion))
        if (savedDraft.conversion_points_attempted) setConversionPointsAttempted(savedDraft.conversion_points_attempted)
        if (savedDraft.conversion_result) setConversionResult(savedDraft.conversion_result)
        if (savedDraft.preceding_touchdown_snap_id) setPrecedingTouchdownSnapId(savedDraft.preceding_touchdown_snap_id)
        if (savedDraft.snap_notes) setSnapNotes(savedDraft.snap_notes)
        if (savedDraft.flagged_for_review !== undefined) setFlaggedForReview(savedDraft.flagged_for_review)
        if (savedDraft.video_segment_id) setVideoSegmentId(savedDraft.video_segment_id)
        if (savedDraft.video_timestamp_mark) setVideoTimestampMark(savedDraft.video_timestamp_mark)
        if (savedDraft.video_offset_seconds) setVideoOffsetSeconds(savedDraft.video_offset_seconds)
        if (savedDraft.snap_started_at) {
          setSnapStartedAt(savedDraft.snap_started_at)
          setHasStartedExplicitly(Boolean(savedDraft.has_started_explicitly))
        }
      }

      // Conteo inicial de operaciones en outbox
      const count = await offlineIndexedDb.countPendingOperations(captureId, teamId, user?.id)
      setPendingOpsCount(count)
      setSyncStatus(count > 0 ? 'saved_local_pending_sync' : 'synced')
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Error al cargar sesión de captura.')
    } finally {
      setLoading(false)
    }
  }, [captureId, teamId, user?.id])

  useEffect(() => {
    loadCaptureSession()
  }, [loadCaptureSession])

  // Guardado progresivo del borrador actual en IndexedDB con debounce
  useEffect(() => {
    if (loading || !capture || isCommittingRef.current) return

    setDraftSaveStatus('saving')

    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current)
      draftTimerRef.current = null
    }

    draftTimerRef.current = setTimeout(async () => {
      // Si el snap está en proceso de commit o ya fue cerrado, no recrear borrador
      if (isCommittingRef.current || lastCommittedSeqRef.current >= snapSequence) {
        return
      }

      const draft: DraftState = {
        capture_id: capture.id,
        team_id: capture.team_id,
        snap_sequence: snapSequence,
        snap_started_at: snapStartedAt,
        has_started_explicitly: hasStartedExplicitly,
        active_offense_slots: activeOffenseSlots,
        active_defense_slots: activeDefenseSlots,
        offense_call_planned: offenseCallPlanned,
        defense_call_planned: defenseCallPlanned,
        offense_call_actual: offenseCallActual,
        defense_call_actual: defenseCallActual,
        primary_result: primaryResult,
        passer_athlete_id: passerAthleteId,
        receiver_athlete_id: receiverAthleteId,
        tackler_athlete_id: tacklerAthleteId,
        explicit_events: explicitEvents,
        current_down: currentDown,
        yard_line_start: yardLineStart,
        yard_line_end: yardLineEnd,
        is_first_down: isFirstDown,
        is_turnover: isTurnover,
        repeat_scenario: repeatScenario,
        is_touchdown: isTouchdown,
        scorer_athlete_id: scorerAthleteId,
        had_lateral: hadLateral,
        conversion_type: conversionType,
        is_conversion: isConversion,
        conversion_points_attempted: conversionPointsAttempted,
        conversion_result: conversionResult,
        preceding_touchdown_snap_id: precedingTouchdownSnapId,
        snap_notes: snapNotes,
        flagged_for_review: flaggedForReview,
        video_segment_id: videoSegmentId,
        video_timestamp_mark: videoTimestampMark,
        video_offset_seconds: videoOffsetSeconds,
        updated_at: new Date().toISOString(),
      }
      try {
        await offlineIndexedDb.saveDraftState(draft)
        if (!isCommittingRef.current && lastCommittedSeqRef.current < snapSequence) {
          setDraftSaveStatus('saved')
        }
      } catch {
        // no bloqueante
      }
    }, 400)

    return () => {
      if (draftTimerRef.current) {
        clearTimeout(draftTimerRef.current)
        draftTimerRef.current = null
      }
    }
  }, [
    loading,
    capture,
    snapSequence,
    snapStartedAt,
    hasStartedExplicitly,
    activeOffenseSlots,
    activeDefenseSlots,
    offenseCallPlanned,
    defenseCallPlanned,
    offenseCallActual,
    defenseCallActual,
    primaryResult,
    passerAthleteId,
    receiverAthleteId,
    tacklerAthleteId,
    explicitEvents,
    currentDown,
    yardLineStart,
    yardLineEnd,
    isFirstDown,
    isTurnover,
    repeatScenario,
    isTouchdown,
    scorerAthleteId,
    hadLateral,
    conversionType,
    isConversion,
    conversionPointsAttempted,
    conversionResult,
    precedingTouchdownSnapId,
    snapNotes,
    flaggedForReview,
    videoSegmentId,
    videoTimestampMark,
    videoOffsetSeconds,
  ])

  // Preparar para usar sin conexión
  const handlePrepareOffline = async () => {
    if (!capture || isPreparing) return
    setIsPreparing(true)
    setPrepareMsg('Descargando contexto deportivo para offline...')
    try {
      const pkg = await fieldCaptureService.prepareCaptureForOffline({
        captureId: capture.id,
        teamId,
        userId: user?.id || 'anonymous',
      })
      setIsPrepared(true)
      setPreparedAt(pkg.prepared_at)
      setPrepareMsg('¡Captura preparada con éxito para usar sin cobertura!')
      setTimeout(() => setPrepareMsg(null), 4000)
    } catch (err: unknown) {
      setPrepareMsg(err instanceof Error ? `Fallo al preparar: ${err.message}` : 'Error al preparar offline')
      setTimeout(() => setPrepareMsg(null), 5000)
    } finally {
      setIsPreparing(false)
    }
  }

  // Cronómetro del bloque
  useEffect(() => {
    if (!capture) return
    const updateElapsed = () => {
      if (capture.status === 'completed' && capture.ended_at) {
        const startMs = new Date(capture.started_at).getTime()
        const endMs = new Date(capture.ended_at).getTime()
        setElapsedBlockSeconds(Math.max(0, Math.floor((endMs - startMs) / 1000)))
        return
      }
      const startMs = new Date(capture.started_at).getTime()
      const nowMs = Date.now()
      const diffSec = Math.max(0, Math.floor((nowMs - startMs) / 1000))
      setElapsedBlockSeconds(diffSec)
    }
    updateElapsed()
    if (capture.status === 'completed') return
    const timer = setInterval(updateElapsed, 1000)
    return () => clearInterval(timer)
  }, [capture])

  const formatTimeMMSS = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60)
    const secs = totalSeconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const isCoordinator =
    capture?.coordinator_device_id === deviceIdRef.current ||
    capture?.coordinator_user_id === user?.id

  const handleTakeoverCoordinator = async () => {
    if (!capture || !user) return
    try {
      setSyncStatus('saving_local')
      const ok = await fieldCaptureService.handoverCoordinator(
        capture.id,
        deviceIdRef.current,
        user.id
      )
      if (ok) {
        setCapture({
          ...capture,
          coordinator_device_id: deviceIdRef.current,
          coordinator_user_id: user.id,
        })
        setSyncStatus('synced')
      }
    } catch (err: unknown) {
      setSyncStatus('needs_attention')
      setErrorMsg(err instanceof Error ? err.message : 'Error al solicitar relevo de coordinador.')
    }
  }

  const handleMarkSnapStart = () => {
    setSnapStartedAt(new Date().toISOString())
    setHasStartedExplicitly(true)
  }

  const handleConfirmPlannedCalls = () => {
    if (offenseCallPlanned) setOffenseCallActual(offenseCallPlanned)
    if (defenseCallPlanned) setDefenseCallActual(defenseCallPlanned)
  }

  // --- SUSTITUCIÓN RÁPIDA (Requerimiento 5) ---
  const handleOpenSubstitution = (unit: 'offense' | 'defense', slot: LineupSlot) => {
    setSubstitutionModal({
      isOpen: true,
      unit,
      slotPosition: slot.position,
      currentSlot: slot,
    })
  }

  const handleConfirmSubstitution = async (params: {
    newAthlete: RosterAthleteOption
    scope: 'this_snap' | 'following_snaps'
    reason?: string
  }) => {
    const isOffense = substitutionModal.unit === 'offense'
    const currentSlots = isOffense ? activeOffenseSlots : activeDefenseSlots

    const updatedSlots = currentSlots.map((s) => {
      if (s.position === substitutionModal.slotPosition) {
        return {
          athlete_id: params.newAthlete.id,
          position: s.position,
          athlete_name: params.newAthlete.full_name,
          dorsal_number: params.newAthlete.dorsal_number,
          primary_position: params.newAthlete.primary_position,
          was_substituted: true,
          substitution_reason: params.scope === 'this_snap' ? 'Puntual (solo este snap)' : 'Persistente',
        }
      }
      return s
    })

    if (isOffense) {
      setActiveOffenseSlots(updatedSlots)
      setIsOffenseCustom(true)
      if (substitutionModal.slotPosition.toUpperCase() === 'QB') {
        setPasserAthleteId(params.newAthlete.id)
      }
    } else {
      setActiveDefenseSlots(updatedSlots)
      setIsDefenseCustom(true)
    }

    // Si es "Mantener en siguientes snaps", actualizar la memoria persistente y servidor
    if (params.scope === 'following_snaps') {
      if (isOffense) {
        setPersistentOffenseSlots(updatedSlots)
      } else {
        setPersistentDefenseSlots(updatedSlots)
      }

      if (capture) {
        try {
          setSyncStatus('saving_local')
          await fieldCaptureService.updateActiveLineups({
            captureId: capture.id,
            offenseLineupName: isOffense ? `${offenseLineupName} (Mod)` : offenseLineupName,
            offenseSlots: isOffense ? updatedSlots : persistentOffenseSlots,
            offenseIsCustom: true,
            defenseLineupName: !isOffense ? `${defenseLineupName} (Mod)` : defenseLineupName,
            defenseSlots: !isOffense ? updatedSlots : persistentDefenseSlots,
            defenseIsCustom: true,
          })
          setSyncStatus('synced')
        } catch (err: unknown) {
          setSyncStatus('needs_attention')
          setErrorMsg(err instanceof Error ? err.message : 'Error al guardar alineación persistente.')
        }
      }
    }
  }

  // Restablecer quinteto a plantilla original
  const handleResetToTemplate = (unit: 'offense' | 'defense') => {
    const isOffense = unit === 'offense'
    const tmplId = isOffense
      ? capture?.offense_lineup_template_id
      : capture?.defense_lineup_template_id

    const tmpl = lineupTemplates.find((t) => t.id === tmplId)
    if (!tmpl) return

    const baseSlots: LineupSlot[] = tmpl.slots.map((s) => {
      const athleteMatch = rosterAthletes.find((a) => a.id === s.athlete_id)
      return {
        athlete_id: s.athlete_id,
        position: s.position,
        athlete_name: s.athlete
          ? `${s.athlete.first_name} ${s.athlete.last_name}`.trim()
          : athleteMatch?.full_name || 'Atleta',
        dorsal_number: s.athlete?.dorsal_number ?? athleteMatch?.dorsal_number ?? null,
        primary_position: s.athlete?.primary_position || athleteMatch?.primary_position || '',
      }
    })

    if (isOffense) {
      setPersistentOffenseSlots(baseSlots)
      setActiveOffenseSlots(baseSlots)
      setIsOffenseCustom(false)
      const qb = baseSlots.find((s) => s.position.toUpperCase() === 'QB')
      if (qb) setPasserAthleteId(qb.athlete_id)
    } else {
      setPersistentDefenseSlots(baseSlots)
      setActiveDefenseSlots(baseSlots)
      setIsDefenseCustom(false)
    }
  }

  const handleSelectLineupTemplate = (unit: 'offense' | 'defense', templateId: string) => {
    const tmpl = lineupTemplates.find((t) => t.id === templateId)
    if (!tmpl) return

    const newSlots: LineupSlot[] = tmpl.slots.map((s) => {
      const athleteMatch = rosterAthletes.find((a) => a.id === s.athlete_id)
      return {
        athlete_id: s.athlete_id,
        position: s.position,
        athlete_name: s.athlete
          ? `${s.athlete.first_name} ${s.athlete.last_name}`.trim()
          : athleteMatch?.full_name || 'Atleta',
        dorsal_number: s.athlete?.dorsal_number ?? athleteMatch?.dorsal_number ?? null,
        primary_position: s.athlete?.primary_position || athleteMatch?.primary_position || '',
      }
    })

    if (unit === 'offense') {
      setPersistentOffenseSlots(newSlots)
      setActiveOffenseSlots(newSlots)
      setOffenseLineupName(tmpl.name)
      setIsOffenseCustom(false)
      const qb = newSlots.find((s) => s.position.toUpperCase() === 'QB')
      if (qb) setPasserAthleteId(qb.athlete_id)
    } else {
      setPersistentDefenseSlots(newSlots)
      setActiveDefenseSlots(newSlots)
      setDefenseLineupName(tmpl.name)
      setIsDefenseCustom(false)
    }
  }

  // --- EVENTOS MÚLTIPLES RÁPIDOS (Requerimiento 1) ---
  const handleSelectAthleteForAction = (athlete: {
    athleteId: string
    name: string
    dorsal: number | null
    unit: 'offense' | 'defense'
    position: string
  }) => {
    if (selectedActor?.athleteId === athlete.athleteId) {
      setSelectedActor(null) // toggle deselección
    } else {
      setSelectedActor(athlete)
    }
  }

  const handleAddEvent = (actionType: SecondaryEventType | 'penalty') => {
    if (!selectedActor) {
      setErrorMsg('Selecciona primero un jugador en el quinteto para registrar su acción.')
      return
    }

    const newEvent: SnapEvent = {
      id: crypto.randomUUID(),
      event_type: actionType,
      actor_athlete_id: selectedActor.athleteId,
      unit: selectedActor.unit,
      registered_at: new Date().toISOString(),
    }

    // Si es tackler de defensa, preasignar tacklerAthleteId si no hay uno
    if (actionType === 'flag_attempt_success' || actionType === 'sack' || actionType === 'interception') {
      if (!tacklerAthleteId) setTacklerAthleteId(selectedActor.athleteId)
    }
    // Si es TD o catch en ataque, preasignar receiverAthleteId
    if ((actionType === 'catch' || actionType === 'touchdown') && !receiverAthleteId) {
      setReceiverAthleteId(selectedActor.athleteId)
    }

    setExplicitEvents((prev) => [...prev, newEvent])
    setSelectedActor(null) // Listo para la siguiente acción de captura rápida
    setErrorMsg(null)
  }

  const handleRemoveExplicitEvent = (indexToRemove: number) => {
    setExplicitEvents((prev) => prev.filter((_, i) => i !== indexToRemove))
  }

  // --- GESTIÓN DE MARCADOR (Requerimiento 4) ---
  const handleUpdateMatchScore = async (side: 'us' | 'rival', points: number) => {
    if (!capture?.match_context) return
    const cur = capture.match_context
    const newContext: MatchContext = {
      ...cur,
      our_score: side === 'us' ? Math.max(0, cur.our_score + points) : cur.our_score,
      rival_score: side === 'rival' ? Math.max(0, cur.rival_score + points) : cur.rival_score,
    }
    setCapture({ ...capture, match_context: newContext })
    try {
      await fieldCaptureService.updateMatchContext(capture.id, newContext)
    } catch {
      // Ignorar fallo secundario
    }
  }

  const handleUpdateSeriesScore = async (side: 'offense' | 'defense', points: number) => {
    if (!capture) return
    const newOff = side === 'offense' ? Math.max(0, seriesScoreOffense + points) : seriesScoreOffense
    const newDef = side === 'defense' ? Math.max(0, seriesScoreDefense + points) : seriesScoreDefense
    setSeriesScoreOffense(newOff)
    setSeriesScoreDefense(newDef)
    try {
      await fieldCaptureService.updateSeriesScore({
        captureId: capture.id,
        offenseScore: newOff,
        defenseScore: newDef,
        showSeriesScore: true,
      })
    } catch {
      // no bloqueante
    }
  }

  // Deshacer borrador
  const handleUndoDraft = (preservePrecedingTd = false) => {
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current)
      draftTimerRef.current = null
    }
    setDraftSaveStatus('pristine')
    setPrimaryResult(null)
    setReceiverAthleteId(null)
    setTacklerAthleteId(null)
    setExplicitEvents([])
    setSelectedActor(null)
    setYardLineEnd(null)
    setIsFirstDown(false)
    setIsTurnover(false)
    setIsTouchdown(false)
    setScorerAthleteId(null)
    setHadLateral(false)
    if (!isConversion && !preservePrecedingTd && !postTdModalOpen) {
      setConversionType(null)
      setConversionPointsAttempted(null)
      setConversionResult(null)
      setPrecedingTouchdownSnapId(null)
    } else if (isConversion) {
      setConversionResult('pending')
    }
    setSnapNotes('')
    setFlaggedForReview(false)
    setHasStartedExplicitly(false)
    if (capture?.id) {
      offlineIndexedDb.clearDraftState(capture.id).catch(() => {})
    }
  }

  // Abrir flujo de registro de conversión manual desde acción visible
  const handleOpenConversionFlow = () => {
    const lastTd = pastSnaps.slice().reverse().find((s) => s.is_touchdown)
    const tdId = precedingTouchdownSnapId || lastTouchdownSnapIdRef.current || lastTd?.id || null
    setPrecedingTouchdownSnapId(tdId)
    setPostTdModalOpen(true)
  }

  // Saltar repetición de guion
  const handleSkipPlannedRep = () => {
    if (!scriptData?.reps || !capture) return
    const nextIndex = capture.current_rep_index + 1
    if (nextIndex < scriptData.reps.length) {
      const nextRep = scriptData.reps[nextIndex]
      setOffenseCallPlanned(nextRep.offense_play_name || '')
      setDefenseCallPlanned(nextRep.defense_call_name || '')
      setOffenseCallActual(nextRep.offense_play_name || '')
      setDefenseCallActual(nextRep.defense_call_name || '')
      setCapture({ ...capture, current_rep_index: nextIndex })
    }
  }

  const handleMarkTeachingRepetition = () => {
    if (pastSnaps.length > 0) {
      const lastSnap = pastSnaps[pastSnaps.length - 1]
      setRepetitionOfSnapId(lastSnap.id)
    }
  }

  // --- FINALIZAR Y AVANZAR AL SIGUIENTE SNAP (Core Commit Flow con guardado durable) ---
  const handleCommitAndNextSnap = async () => {
    if (!capture) return

    // Cancelar inmediatamente cualquier timer diferido de autoguardado para evitar que recree el borrador
    isCommittingRef.current = true
    lastCommittedSeqRef.current = snapSequence
    if (draftTimerRef.current) {
      clearTimeout(draftTimerRef.current)
      draftTimerRef.current = null
    }

    setSyncStatus('saving_local')
    setErrorMsg(null)

    try {
      // 1. Validar 10 participantes (5 ataque y 5 defensa)
      if (activeOffenseSlots.length !== 5 || activeDefenseSlots.length !== 5) {
        throw new Error('Se requieren exactamente 5 jugadores de ataque y 5 de defensa para registrar el snap.')
      }

      const offIds = activeOffenseSlots.map((s) => s.athlete_id)
      const defIds = activeDefenseSlots.map((s) => s.athlete_id)
      const duplicates = offIds.filter((id) => defIds.includes(id))
      if (duplicates.length > 0) {
        throw new Error('Una persona no puede participar en ataque y defensa en el mismo snap 5v5.')
      }

      // 2. Participantes inmutables del snap
      const participants: SnapParticipant[] = [
        ...activeOffenseSlots.map((s) => ({
          unit: 'offense' as const,
          position: s.position,
          athlete_id: s.athlete_id,
          athlete_name: s.athlete_name || 'Atleta',
          dorsal_number: s.dorsal_number,
          was_substituted: Boolean(s.was_substituted),
          substitution_reason: s.substitution_reason || null,
        })),
        ...activeDefenseSlots.map((s) => ({
          unit: 'defense' as const,
          position: s.position,
          athlete_id: s.athlete_id,
          athlete_name: s.athlete_name || 'Atleta',
          dorsal_number: s.dorsal_number,
          was_substituted: Boolean(s.was_substituted),
          substitution_reason: s.substitution_reason || null,
        })),
      ]

      // 3. Ganancia neta calculada (En Touchdown, yarda final fijada en línea de gol 50)
      const effectiveYardEnd = isTouchdown ? 50 : yardLineEnd
      const netYards = isTouchdown ? (50 - yardLineStart) : calculateNetYards(yardLineStart, effectiveYardEnd)

      // Anotador efectivo si no fue seleccionado explícitamente
      const effectiveScorerId = isTouchdown
        ? (scorerAthleteId || (primaryResult === 'pass_complete' ? receiverAthleteId : (primaryResult === 'run' ? passerAthleteId : null)))
        : null

      // 4. Tiempo transcurrido en el bloque
      const startMs = new Date(capture.started_at).getTime()
      const snapMs = new Date(snapStartedAt).getTime()
      const elapsedSec = Math.max(0, Math.floor((snapMs - startMs) / 1000))

      // 5. Payload de snap enriquecido (inicio real marcado o null si no se marcó)
      const snapPayload: Partial<FieldSnap> = {
        id: crypto.randomUUID(),
        organization_id: capture.organization_id,
        team_id: capture.team_id,
        capture_id: capture.id,
        sequence: snapSequence,
        repetition_of_snap_id: repetitionOfSnapId,
        mode: capture.mode,
        our_side: unitView,
        status: 'finalized',
        validity: 'valid',
        coverage: primaryResult ? 'detailed' : 'minimal',
        flagged_for_review: flaggedForReview,
        primary_result: primaryResult,

        // Down y yardas
        down: currentDown,
        yard_line_start: yardLineStart,
        yard_line_end: effectiveYardEnd,
        net_yards: netYards,
        is_first_down: isFirstDown,
        is_turnover: isTurnover,
        turnover_type: isTurnover ? (primaryResult === 'interception' ? 'interception' : 'downs') : null,

        // Touchdown, anotador y conversiones explícitas (M8C)
        is_touchdown: isConversion ? false : isTouchdown,
        scorer_athlete_id: effectiveScorerId,
        had_lateral: isTouchdown && !isConversion ? hadLateral : false,
        conversion_type: conversionType || null,
        is_conversion: isConversion,
        conversion_points_attempted: isConversion ? (conversionPointsAttempted || (conversionType === '2_points' ? 2 : 1)) : null,
        conversion_result: isConversion
          ? (conversionResult || 'pending')
          : null,
        preceding_touchdown_snap_id: isConversion ? precedingTouchdownSnapId : null,
        points_awarded: isConversion
          ? (conversionResult === 'successful' && !repeatScenario ? (conversionPointsAttempted || (conversionType === '2_points' ? 2 : 1)) : 0)
          : (isTouchdown && (capture.mode === 'match' || showSeriesScore) ? 6 : 0),

        offense_call_planned: offenseCallPlanned || null,
        offense_call_actual: offenseCallActual || null,
        defense_call_planned: defenseCallPlanned || null,
        defense_call_actual: defenseCallActual || null,
        offense_lineup_name: offenseLineupName,
        defense_lineup_name: defenseLineupName,
        passer_athlete_id: passerAthleteId || null,
        receiver_athlete_id: receiverAthleteId || null,
        tackler_athlete_id: tacklerAthleteId || null,
        snap_started_at: hasStartedExplicitly ? snapStartedAt : undefined,
        registered_at: new Date().toISOString(),
        elapsed_seconds_in_block: elapsedSec,
        video_segment_id: videoSegmentId,
        video_timestamp_mark: videoTimestampMark,
        video_offset_seconds: videoOffsetSeconds,
        notes: snapNotes.trim() || null,
        revision: 1,
      }

      // 6. Calcular actualización del marcador del partido si aplica
      let updatedMatchContext: MatchContext | undefined = undefined
      if (capture.mode === 'match' && capture.match_context) {
        let scoreDelta = 0
        if (snapPayload.is_touchdown) {
          scoreDelta = 6
        } else if (isConversion && conversionResult === 'successful' && !repeatScenario) {
          scoreDelta = conversionPointsAttempted || (conversionType === '2_points' ? 2 : 1)
        }

        if (scoreDelta > 0) {
          updatedMatchContext = {
            ...capture.match_context,
            our_score: capture.match_context.our_score + scoreDelta,
          }
        }
      }

      // Guardado durable en IndexedDB + Outbox (confirmación real tras persistencia local)
      const committed = await fieldCaptureService.saveSnapLocallyAndEnqueue({
        snap: snapPayload,
        participants,
        events: explicitEvents,
        teamId,
        userId: user?.id || 'anonymous',
        updatedCapture: updatedMatchContext ? { id: capture.id, match_context: updatedMatchContext } : undefined,
      })

      if (updatedMatchContext) {
        setCapture((prev) => prev ? { ...prev, match_context: updatedMatchContext } : prev)
        fieldCaptureService.updateMatchContext(capture.id, updatedMatchContext).catch(() => {})
      }

      // 7. Actualizar lista de snaps pasados con la versión local confirmada
      setPastSnaps((prev) => [...prev, committed])

      // 8. CÁLCULO DE SIGUIENTE DOWN Y YARDA (M4 / M8C Conversiones)
      if (committed.is_touchdown) {
        lastTouchdownSnapIdRef.current = committed.id
        setPrecedingTouchdownSnapId(committed.id)
        setPostTdModalOpen(true)
      } else if (isConversion) {
        if (repeatScenario) {
          // No-play o repetición: el intento no se consume estadísticamente y se repite
          setConversionResult('pending')
        } else {
          // Intento consumido: reiniciar para la nueva serie ordinaria del rival
          setIsConversion(false)
          setConversionPointsAttempted(null)
          setConversionResult(null)
          setConversionType(null)
          setPrecedingTouchdownSnapId(null)
          setCurrentDown(1)
          setYardLineStart(5)
        }
      } else if (!repeatScenario) {
        if (isTurnover) {
          const nextStart = effectiveYardEnd !== null ? Math.max(0, Math.min(50, 50 - effectiveYardEnd)) : 5
          setCurrentDown(1)
          setYardLineStart(nextStart)
        } else if (isFirstDown) {
          const nextStart = effectiveYardEnd !== null ? effectiveYardEnd : yardLineStart
          setCurrentDown(1)
          setYardLineStart(nextStart)
        } else if (currentDown < 4) {
          const nextStart = effectiveYardEnd !== null ? effectiveYardEnd : yardLineStart
          setCurrentDown(currentDown + 1)
          setYardLineStart(nextStart)
        } else {
          const nextStart = effectiveYardEnd !== null ? effectiveYardEnd : yardLineStart
          setCurrentDown(1)
          setYardLineStart(nextStart)
        }
      }
      setYardLineEnd(null)
      setIsFirstDown(false)
      setIsTurnover(false)

      // 9. RESTAURACIÓN DE SUSTITUCIONES PUNTUALES ("Solo este snap")
      setActiveOffenseSlots([...persistentOffenseSlots])
      setActiveDefenseSlots([...persistentDefenseSlots])

      // 10. Avanzar secuencia y guion
      const nextSeq = snapSequence + 1
      setSnapSequence(nextSeq)
      setSnapStartedAt(new Date().toISOString())
      setHasStartedExplicitly(false)
      setRepetitionOfSnapId(null)

      if (scriptData?.reps && capture.current_rep_index + 1 < scriptData.reps.length) {
        const nextRepIndex = capture.current_rep_index + 1
        const nextRep = scriptData.reps[nextRepIndex]
        setOffenseCallPlanned(nextRep.offense_play_name || '')
        setDefenseCallPlanned(nextRep.defense_call_name || '')
        setOffenseCallActual(nextRep.offense_play_name || '')
        setDefenseCallActual(nextRep.defense_call_name || '')
        setCapture((prev) => prev ? {
          ...prev,
          current_snap_sequence: nextSeq,
          current_rep_index: nextRepIndex,
        } : prev)
      } else {
        // En captura libre o partido: limpiar llamadas del snap anterior para el nuevo snap
        setOffenseCallPlanned('')
        setDefenseCallPlanned('')
        setOffenseCallActual('')
        setDefenseCallActual('')
        setCapture((prev) => prev ? {
          ...prev,
          current_snap_sequence: nextSeq,
        } : prev)
      }

      // 11. Limpiar borrador de eventos y selecciones (preservando precedingTouchdownSnapId si hay TD)
      handleUndoDraft(Boolean(committed.is_touchdown))

      // Limpiar borrador persistente en IndexedDB
      await offlineIndexedDb.clearDraftState(capture.id)
      setDraftSaveStatus('pristine')

      // Actualizar conteo de pendientes
      await refreshPendingCount()
    } catch (err: unknown) {
      setSyncStatus('needs_attention')
      setErrorMsg(err instanceof Error ? err.message : 'Error al guardar snap en dispositivo.')
    } finally {
      isCommittingRef.current = false
    }
  }

  // Auditoría: corrección de resultado o detalles en caliente
  const handleCorrectPastSnapResult = async (params: {
    snapId: string
    newPrimaryResult: PrimaryResult
    passerId?: string | null
    receiverId?: string | null
    tacklerId?: string | null
  }) => {
    try {
      setSyncStatus('saving_local')
      const oldSnap = pastSnaps.find((s) => s.id === params.snapId)
      const updated = await fieldCaptureService.correctSnapResult({
        snapId: params.snapId,
        newPrimaryResult: params.newPrimaryResult,
        passerAthleteId: params.passerId,
        receiverAthleteId: params.receiverId,
        tacklerAthleteId: params.tacklerId,
      })
      setPastSnaps((prev) => prev.map((s) => (s.id === params.snapId ? updated : s)))
      setSelectedPastSnap(updated)

      if (capture?.mode === 'match' && capture.match_context && oldSnap) {
        const oldPts = oldSnap.points_awarded || 0
        const newPts = updated.points_awarded || 0
        const delta = newPts - oldPts
        if (delta !== 0) {
          const newCtx: MatchContext = {
            ...capture.match_context,
            our_score: Math.max(0, capture.match_context.our_score + delta),
          }
          setCapture({ ...capture, match_context: newCtx })
          fieldCaptureService.updateMatchContext(capture.id, newCtx).catch(() => {})
        }
      }

      await refreshPendingCount()
    } catch (err: unknown) {
      setSyncStatus('needs_attention')
      setErrorMsg(err instanceof Error ? err.message : 'Error al corregir resultado del snap.')
    }
  }

  const handleUpdatePastSnapDetails = async (params: {
    snapId: string
    patch: Partial<FieldSnap>
    events?: SnapEvent[]
  }) => {
    try {
      setSyncStatus('saving_local')
      const updated = await fieldCaptureService.updateSnapDetails(params)
      setPastSnaps((prev) => prev.map((s) => (s.id === params.snapId ? updated : s)))
      setSelectedPastSnap(updated)
      await refreshPendingCount()
    } catch (err: unknown) {
      setSyncStatus('needs_attention')
      setErrorMsg(err instanceof Error ? err.message : 'Error al actualizar detalles del snap.')
    }
  }

  const handleVoidPastSnap = async (snapId: string, validity: 'void' | 'no_play', reason?: string) => {
    try {
      setSyncStatus('saving_local')
      const targetSnap = pastSnaps.find((s) => s.id === snapId)
      await fieldCaptureService.saveSnapVoidLocally({
        snapId,
        captureId,
        validity,
        reason,
        teamId,
        userId: user?.id || 'anonymous',
      })
      setPastSnaps((prev) =>
        prev.map((s) => (s.id === snapId ? { ...s, validity, review_notes: reason } : s))
      )
      if (selectedPastSnap?.id === snapId) {
        setSelectedPastSnap({ ...selectedPastSnap, validity, review_notes: reason })
      }

      if (capture?.mode === 'match' && capture.match_context && targetSnap && targetSnap.points_awarded) {
        const newCtx: MatchContext = {
          ...capture.match_context,
          our_score: Math.max(0, capture.match_context.our_score - targetSnap.points_awarded),
        }
        setCapture({ ...capture, match_context: newCtx })
        fieldCaptureService.updateMatchContext(capture.id, newCtx).catch(() => {})
      }

      await refreshPendingCount()
    } catch (err: unknown) {
      setSyncStatus('needs_attention')
      setErrorMsg(err instanceof Error ? err.message : 'Error al anular snap.')
    }
  }

  if (loading) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--nav-bg-base)',
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1200,
        }}
      >
        <RefreshCw size={28} className="spin" style={{ marginBottom: '1rem', color: 'var(--brand-red)' }} />
        <div style={{ fontSize: '0.95rem', fontWeight: 600 }}>Cargando consola de campo...</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--nav-text-muted)' }}>
          Recuperando estado de bloque, down y quintetos
        </div>
      </div>
    )
  }

  const primaryResultButtons: { id: PrimaryResult; label: string; color: string }[] = [
    { id: 'pass_complete', label: 'Pase Completo', color: 'var(--status-success-bg)' },
    { id: 'pass_incomplete', label: 'Pase Incompleto', color: 'var(--surface-subtle)' },
    { id: 'run', label: 'Carrera', color: 'var(--brand-yellow-subtle)' },
    { id: 'sack', label: 'Sack', color: 'var(--brand-red-subtle)' },
    { id: 'interception', label: 'Intercepción', color: 'var(--brand-red-subtle)' },
    { id: 'other', label: 'Otro / Penalización', color: 'var(--surface-subtle)' },
  ]

  const netYardsCurrent = calculateNetYards(yardLineStart, yardLineEnd)

  return (
    <div
      className="mobile-viewport-console"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'var(--surface-ground)',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {/* 1. CABECERA COMPACTA DE CAMPO (Azul Noche Oficial) */}
      <header
        className="field-console-header"
        style={{
          backgroundColor: 'var(--nav-bg-base)',
          color: 'var(--nav-text)',
          borderBottom: '1px solid var(--nav-border)',
          padding: '0.6rem 0.85rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
        }}
      >
        {/* Lado izquierdo: Snap # y cronómetro del bloque */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div
            style={{
              backgroundColor: 'var(--brand-red)',
              color: '#fff',
              fontSize: '0.95rem',
              fontWeight: 800,
              padding: '0.25rem 0.65rem',
              borderRadius: '6px',
              fontVariantNumeric: 'tabular-nums',
              letterSpacing: '0.02em',
            }}
          >
            Snap #{snapSequence}
          </div>

          {draftSaveStatus === 'saving' && (
            <span
              style={{
                fontSize: '0.65rem',
                color: '#facc15',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem',
                backgroundColor: 'rgba(234, 179, 8, 0.15)',
                border: '1px solid rgba(234, 179, 8, 0.35)',
                padding: '0.15rem 0.45rem',
                borderRadius: '4px',
                fontWeight: 600,
              }}
              title="Guardando cambios del snap en IndexedDB local (400ms debounce)"
            >
              <RefreshCw size={10} className="spin" />
              <span>Guardando...</span>
            </span>
          )}
          {draftSaveStatus === 'saved' && (
            <span
              style={{
                fontSize: '0.65rem',
                color: '#4ade80',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.2rem',
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                border: '1px solid rgba(34, 197, 94, 0.35)',
                padding: '0.15rem 0.45rem',
                borderRadius: '4px',
                fontWeight: 600,
              }}
              title="Borrador persistido en IndexedDB local"
            >
              <CheckCircle2 size={10} />
              <span>Guardado local</span>
            </span>
          )}

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span
              style={{
                fontSize: '0.85rem',
                fontWeight: 700,
                color: '#fff',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatTimeMMSS(elapsedBlockSeconds)}
            </span>
            <span style={{ fontSize: '0.65rem', color: 'var(--nav-text-muted)' }}>
              {capture?.title || 'Bloque Team'}
            </span>
          </div>
        </div>

        {/* Lado derecho: Estado de red, marcas de vídeo, historial y salida */}
        <div className="field-console-tools" style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
          {isCoordinator ? (
            <span
              className="badge"
              style={{
                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                color: '#4ade80',
                border: '1px solid #22c55e',
                fontSize: '0.65rem',
                padding: '0.15rem 0.4rem',
              }}
              title="Este dispositivo coordina la secuencia de snaps"
            >
              Coordinador
            </span>
          ) : (
            <button
              type="button"
              onClick={handleTakeoverCoordinator}
              style={{
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                color: '#60a5fa',
                border: '1px solid #3b82f6',
                borderRadius: '4px',
                fontSize: '0.65rem',
                padding: '0.2rem 0.45rem',
                cursor: 'pointer',
              }}
              title="Pulsar para tomar el control de la secuencia"
            >
              Relevar Coord
            </button>
          )}

          {/* Botón Preparar para usar sin conexión (M5 Requerimiento 1) */}
          <button
            type="button"
            onClick={handlePrepareOffline}
            disabled={isPreparing}
            style={{
              backgroundColor: isPrepared ? 'rgba(34, 197, 94, 0.15)' : 'var(--nav-bg-surface)',
              color: isPrepared ? '#4ade80' : 'var(--nav-text-muted)',
              border: isPrepared ? '1px solid rgba(34, 197, 94, 0.4)' : '1px solid var(--nav-border)',
              borderRadius: '6px',
              padding: '0.25rem 0.5rem',
              fontSize: '0.68rem',
              fontWeight: 600,
              cursor: isPreparing ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              minHeight: '32px',
            }}
            title={
              preparedAt
                ? `Preparado offline: ${new Date(preparedAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}. Haz clic para actualizar.`
                : 'Descargar datos deportivos para operar sin cobertura'
            }
          >
            {isPreparing ? (
              <RefreshCw size={12} className="spin" />
            ) : (
              <Download size={12} />
            )}
            <span>
              {isPreparing
                ? 'Preparando...'
                : isPrepared
                ? 'Offline listo'
                : 'Preparar offline'}
            </span>
          </button>

          {/* Badge interactivo de Sincronización e IndexedDB con acceso a detalle (M5 Requerimiento 5) */}
          <button
            type="button"
            onClick={() => setSyncModalOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.3rem',
              backgroundColor:
                syncStatus === 'synced'
                  ? 'rgba(34, 197, 94, 0.15)'
                  : syncStatus === 'saving_local'
                  ? 'rgba(234, 179, 8, 0.15)'
                  : syncStatus === 'syncing'
                  ? 'rgba(59, 130, 246, 0.2)'
                  : syncStatus === 'saved_local_pending_sync'
                  ? 'rgba(249, 115, 22, 0.15)'
                  : 'rgba(239, 68, 68, 0.2)',
              color:
                syncStatus === 'synced'
                  ? '#4ade80'
                  : syncStatus === 'saving_local'
                  ? 'var(--brand-yellow)'
                  : syncStatus === 'syncing'
                  ? '#60a5fa'
                  : syncStatus === 'saved_local_pending_sync'
                  ? '#fb923c'
                  : '#f87171',
              border:
                syncStatus === 'synced'
                  ? '1px solid rgba(34, 197, 94, 0.35)'
                  : syncStatus === 'saving_local'
                  ? '1px solid rgba(234, 179, 8, 0.35)'
                  : syncStatus === 'syncing'
                  ? '1px solid rgba(59, 130, 246, 0.4)'
                  : syncStatus === 'saved_local_pending_sync'
                  ? '1px solid rgba(249, 115, 22, 0.4)'
                  : '1px solid rgba(239, 68, 68, 0.4)',
              borderRadius: '6px',
              padding: '0.25rem 0.5rem',
              fontSize: '0.68rem',
              fontWeight: 700,
              cursor: 'pointer',
              minHeight: '32px',
            }}
            title="Ver cola de sincronización y estado IndexedDB"
          >
            {syncStatus === 'saving_local' ? (
              <>
                <RefreshCw size={12} className="spin" />
                <span>Guardando...</span>
              </>
            ) : syncStatus === 'saved_local_pending_sync' ? (
              <>
                <HardDrive size={12} />
                <span>Local ({pendingOpsCount})</span>
              </>
            ) : syncStatus === 'syncing' ? (
              <>
                <RefreshCw size={12} className="spin" />
                <span>Sync ({pendingOpsCount})</span>
              </>
            ) : syncStatus === 'synced' ? (
              <>
                <CheckCircle2 size={12} />
                <span>Sincronizado</span>
              </>
            ) : (
              <>
                <AlertTriangle size={12} />
                <span>Atención ({pendingOpsCount})</span>
              </>
            )}
          </button>

          {/* Botón Marca de Vídeo */}
          <button
            type="button"
            onClick={() => setVideoModalOpen(true)}
            style={{
              backgroundColor: videoTimestampMark ? 'var(--brand-red)' : 'var(--nav-bg-surface)',
              color: '#fff',
              border: '1px solid var(--nav-border)',
              borderRadius: '6px',
              padding: '0.35rem 0.55rem',
              fontSize: '0.72rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              minHeight: '34px',
            }}
            title="Vincular referencia de vídeo manual"
          >
            <Video size={13} />
            <span>{videoTimestampMark || 'Vídeo'}</span>
          </button>

          {/* Botón Finalizar Partido / Bloque (Ajuste M4 - Requerimiento 2) */}
          {capture?.status !== 'completed' && (
            <button
              type="button"
              onClick={() => setFinalizeModalOpen(true)}
              style={{
                backgroundColor: 'transparent',
                color: '#fca5a5',
                border: '1px solid rgba(239, 68, 68, 0.4)',
                borderRadius: '6px',
                padding: '0.35rem 0.55rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                minHeight: '34px',
              }}
              title={capture?.mode === 'match' ? 'Finalizar partido oficial' : 'Finalizar bloque de entrenamiento'}
            >
              <Flag size={13} />
              <span>{capture?.mode === 'match' ? 'Finalizar partido' : 'Finalizar bloque'}</span>
            </button>
          )}

          {/* Botón Historial de Snaps Completo (Ajuste M4 - Requerimiento 3) */}
          <button
            type="button"
            onClick={() => setHistoryModalOpen(true)}
            style={{
              backgroundColor: 'var(--nav-bg-surface)',
              color: '#fff',
              border: '1px solid var(--nav-border)',
              borderRadius: '6px',
              padding: '0.35rem 0.55rem',
              fontSize: '0.72rem',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              minHeight: '34px',
            }}
            title="Ver historial cronológico snap a snap"
          >
            <History size={13} />
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>{pastSnaps.length}</span>
          </button>

          {/* Salir */}
          <button
            type="button"
            onClick={() => {
              if (primaryResult || hasStartedExplicitly || explicitEvents.length > 0) {
                setExitPromptOpen(true)
              } else {
                onClose()
              }
            }}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--nav-text-muted)',
              cursor: 'pointer',
              padding: '0.35rem',
            }}
            aria-label="Salir de la consola"
          >
            <X size={18} />
          </button>
        </div>
      </header>

      {/* Alerta de coordinación multi-pestaña */}
      {multiTabConflict && (
        <div
          style={{
            backgroundColor: 'var(--brand-yellow-subtle)',
            color: 'var(--brand-yellow)',
            borderBottom: '1px solid var(--brand-yellow)',
            padding: '0.4rem 0.85rem',
            fontSize: '0.74rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '0.45rem',
          }}
        >
          <AlertTriangle size={14} />
          <span>
            Atención: Esta captura está abierta en otra pestaña del navegador. Para evitar envíos duplicados o desincronización, utiliza una sola pestaña.
          </span>
        </div>
      )}

      {/* Toast informativo de preparación offline */}
      {prepareMsg && (
        <div
          style={{
            backgroundColor: 'rgba(34, 197, 94, 0.15)',
            color: '#4ade80',
            borderBottom: '1px solid rgba(34, 197, 94, 0.3)',
            padding: '0.35rem 0.85rem',
            fontSize: '0.72rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: '0.45rem',
          }}
        >
          <Check size={13} />
          <span>{prepareMsg}</span>
        </div>
      )}

      {/* 2. MARCADOR VISIBLE (PC & Móvil - Requerimiento 4) */}
      {/* 2A. MODO PARTIDO (España vs Rival) */}
      {capture?.mode === 'match' && capture.match_context && (
        <div
          style={{
            backgroundColor: 'var(--nav-bg-surface)',
            color: '#fff',
            padding: '0.45rem 0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--nav-border)',
            fontSize: '0.82rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span style={{ fontWeight: 800, color: 'var(--brand-red)' }}>España</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
              <button
                type="button"
                onClick={() => handleUpdateMatchScore('us', 6)}
                style={{
                  fontSize: '0.68rem',
                  padding: '0.15rem 0.4rem',
                  borderRadius: '3px',
                  backgroundColor: 'var(--brand-red)',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
                title="Ajuste manual (+6). Nota: registrar un Touchdown suma automáticamente +6."
              >
                +6
              </button>
              <button
                type="button"
                onClick={() => handleUpdateMatchScore('us', 1)}
                style={{
                  fontSize: '0.68rem',
                  padding: '0.15rem 0.35rem',
                  borderRadius: '3px',
                  backgroundColor: 'var(--nav-border)',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                }}
                title="Ajuste manual (+1)"
              >
                +1
              </button>
              <button
                type="button"
                onClick={() => handleUpdateMatchScore('us', -1)}
                style={{
                  fontSize: '0.68rem',
                  padding: '0.15rem 0.35rem',
                  borderRadius: '3px',
                  backgroundColor: 'transparent',
                  color: 'var(--text-muted)',
                  border: '1px solid var(--nav-border)',
                  cursor: 'pointer',
                }}
                title="Ajuste manual (-1)"
              >
                -1
              </button>
            </div>

            <span
              style={{
                fontSize: '1.15rem',
                fontWeight: 900,
                fontVariantNumeric: 'tabular-nums',
                color: '#fff',
                padding: '0 0.3rem',
              }}
            >
              {capture.match_context.our_score} - {capture.match_context.rival_score}
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
              <button
                type="button"
                onClick={() => handleUpdateMatchScore('rival', 6)}
                style={{
                  fontSize: '0.68rem',
                  padding: '0.15rem 0.4rem',
                  borderRadius: '3px',
                  backgroundColor: 'var(--nav-border)',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                  fontWeight: 700,
                }}
                title="Ajuste manual rival (+6)"
              >
                +6
              </button>
              <button
                type="button"
                onClick={() => handleUpdateMatchScore('rival', 1)}
                style={{
                  fontSize: '0.68rem',
                  padding: '0.15rem 0.35rem',
                  borderRadius: '3px',
                  backgroundColor: 'var(--nav-border)',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                }}
                title="Ajuste manual rival (+1)"
              >
                +1
              </button>
              <button
                type="button"
                onClick={() => handleUpdateMatchScore('rival', -1)}
                style={{
                  fontSize: '0.68rem',
                  padding: '0.15rem 0.35rem',
                  borderRadius: '3px',
                  backgroundColor: 'transparent',
                  color: 'var(--text-muted)',
                  border: '1px solid var(--nav-border)',
                  cursor: 'pointer',
                }}
                title="Ajuste manual rival (-1)"
              >
                -1
              </button>
            </div>

            <span style={{ fontWeight: 700, color: '#e2e8f0' }}>
              {capture.match_context.rival_name || 'Rival'}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', fontSize: '0.72rem' }}>
            {!isConversion && (
              <button
                type="button"
                onClick={handleOpenConversionFlow}
                style={{
                  backgroundColor: 'rgba(234, 88, 12, 0.22)',
                  border: '1px solid rgba(234, 88, 12, 0.7)',
                  borderRadius: '6px',
                  color: '#fed7aa',
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  padding: '0.2rem 0.6rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                  minHeight: '28px',
                }}
                title="Registrar intento de conversión de 1 o 2 puntos"
              >
                <span>🏉 Registrar conversión</span>
              </button>
            )}
            <span className="badge badge-neutral">{capture.match_context.period}</span>
          </div>
        </div>
      )}

      {/* 2B. MODO TEAM COMPETITIVO (Marcador de Serie: Ataque vs Defensa) */}
      {(capture?.mode === 'competitive_team' || showSeriesScore) && (
        <div
          style={{
            backgroundColor: 'var(--nav-bg-surface)',
            color: '#fff',
            padding: '0.4rem 0.85rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--nav-border)',
            fontSize: '0.8rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
            <span style={{ fontSize: '0.7rem', color: 'var(--nav-text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
              Serie
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <strong style={{ color: 'var(--brand-red)' }}>Ataque</strong>
              <button
                type="button"
                onClick={() => handleUpdateSeriesScore('offense', 6)}
                style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', backgroundColor: 'var(--brand-red)', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 700 }}
              >
                +6
              </button>
              <button
                type="button"
                onClick={() => handleUpdateSeriesScore('offense', 1)}
                style={{ fontSize: '0.65rem', padding: '0.1rem 0.3rem', backgroundColor: 'var(--nav-border)', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer' }}
              >
                +1
              </button>
              <button
                type="button"
                onClick={() => handleUpdateSeriesScore('offense', -1)}
                style={{ fontSize: '0.65rem', padding: '0.1rem 0.25rem', backgroundColor: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--nav-border)', borderRadius: '3px', cursor: 'pointer' }}
              >
                -1
              </button>
            </div>

            <span style={{ fontSize: '1.05rem', fontWeight: 900, fontVariantNumeric: 'tabular-nums', color: '#fff' }}>
              {seriesScoreOffense} - {seriesScoreDefense}
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <button
                type="button"
                onClick={() => handleUpdateSeriesScore('defense', 2)}
                style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', backgroundColor: 'var(--nav-border)', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 700 }}
                title="Sack o Parada (+2)"
              >
                +2
              </button>
              <button
                type="button"
                onClick={() => handleUpdateSeriesScore('defense', 6)}
                style={{ fontSize: '0.65rem', padding: '0.1rem 0.35rem', backgroundColor: 'var(--nav-border)', color: '#fff', border: 'none', borderRadius: '3px', cursor: 'pointer', fontWeight: 700 }}
                title="Pick-6 o TD Defensa (+6)"
              >
                +6
              </button>
              <button
                type="button"
                onClick={() => handleUpdateSeriesScore('defense', -1)}
                style={{ fontSize: '0.65rem', padding: '0.1rem 0.25rem', backgroundColor: 'transparent', color: 'var(--text-muted)', border: '1px solid var(--nav-border)', borderRadius: '3px', cursor: 'pointer' }}
              >
                -1
              </button>
              <strong style={{ color: '#93c5fd' }}>Defensa</strong>
            </div>
          </div>

          <div style={{ fontSize: '0.7rem', color: 'var(--nav-text-muted)' }}>
            Tanteo de entrenamiento independiente
          </div>
        </div>
      )}

      {/* ALERTA DE ERROR SI EXISTE */}
      {errorMsg && (
        <div
          style={{
            backgroundColor: 'var(--brand-red-subtle)',
            color: 'var(--brand-red)',
            padding: '0.45rem 0.85rem',
            fontSize: '0.75rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid var(--brand-red)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <AlertTriangle size={14} />
            <span>{errorMsg}</span>
          </div>
          <button
            type="button"
            onClick={() => setErrorMsg(null)}
            style={{ background: 'none', border: 'none', color: 'var(--brand-red)', cursor: 'pointer' }}
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* 2C. BANNER DE SESIÓN FINALIZADA (Ajuste M4 - Requerimiento 2) */}
      {capture?.status === 'completed' && (
        <div
          style={{
            backgroundColor: 'var(--brand-blue-night)',
            color: '#fff',
            padding: '0.65rem 0.85rem',
            borderBottom: '2px solid var(--brand-yellow)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.5rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span
              style={{
                backgroundColor: 'var(--status-success-bg)',
                color: 'var(--status-success-text)',
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                fontSize: '0.72rem',
                fontWeight: 800,
                textTransform: 'uppercase',
              }}
            >
              {capture.mode === 'match' ? 'Partido Finalizado' : 'Bloque Finalizado'}
            </span>
            {capture.ended_at && (
              <span style={{ fontSize: '0.74rem', color: 'var(--nav-text-muted)' }}>
                Finalizado a las {new Date(capture.ended_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            <span style={{ fontSize: '0.74rem', color: '#cbd5e1' }}>
              · {pastSnaps.length} snaps registrados
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <button
              type="button"
              onClick={() => setHistoryModalOpen(true)}
              style={{
                backgroundColor: 'var(--brand-red)',
                color: '#fff',
                border: 'none',
                borderRadius: '5px',
                padding: '0.3rem 0.6rem',
                fontSize: '0.72rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
              }}
            >
              <History size={13} />
              Revisar historial snap a snap
            </button>
            {isCoordinator && (
              <button
                type="button"
                onClick={() => setReopenModalOpen(true)}
                style={{
                  backgroundColor: 'transparent',
                  color: '#fff',
                  border: '1px solid var(--nav-border)',
                  borderRadius: '5px',
                  padding: '0.3rem 0.6rem',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Reabrir para continuar
              </button>
            )}
          </div>
        </div>
      )}

      {/* 3. ÁREA DE TRABAJO PRINCIPAL CON SCROLL CONTROLADO */}
      <main
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '0.6rem 0.75rem 6.5rem',
          maxWidth: '720px',
          margin: '0 auto',
          width: '100%',
        }}
      >
        {/* SELECTOR SEGMENTADO DE VISTA: ATAQUE | AMBAS | DEFENSA */}
        <div
          style={{
            display: 'flex',
            backgroundColor: 'var(--surface-subtle)',
            padding: '0.2rem',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--surface-border)',
            marginBottom: '0.6rem',
          }}
        >
          {(
            [
              { id: 'offense', label: 'Ataque' },
              { id: 'both', label: 'Ambas Unidades (5v5)' },
              { id: 'defense', label: 'Defensa' },
            ] as { id: 'both' | 'offense' | 'defense'; label: string }[]
          ).map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => setUnitView(v.id)}
              style={{
                flex: 1,
                fontSize: '0.75rem',
                fontWeight: unitView === v.id ? 700 : 500,
                padding: '0.35rem 0.2rem',
                borderRadius: '4px',
                border: 'none',
                backgroundColor: unitView === v.id ? 'var(--surface-card)' : 'transparent',
                color: unitView === v.id ? 'var(--text-main)' : 'var(--text-secondary)',
                boxShadow: unitView === v.id ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                cursor: 'pointer',
                minHeight: '32px',
              }}
            >
              {v.label}
            </button>
          ))}
        </div>

        {/* 3B. SECCIÓN DOWN, POSICIÓN DE CAMPO Y YARDAS (Requerimiento 3) */}
        <div
          style={{
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '0.65rem 0.75rem',
            marginBottom: '0.6rem',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          {/* Banner de Intento de Conversión (M8C) */}
          {isConversion && (
            <div
              style={{
                backgroundColor: 'rgba(234, 88, 12, 0.08)',
                border: '1.5px solid rgba(234, 88, 12, 0.45)',
                borderRadius: '8px',
                padding: '0.65rem 0.75rem',
                marginBottom: '0.65rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
              }}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span
                    style={{
                      backgroundColor: '#ea580c',
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      padding: '0.2rem 0.5rem',
                      borderRadius: '4px',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {conversionPointsAttempted === 2 ? 'Conversión de 2 puntos' : 'Conversión de 1 punto'}
                  </span>
                  <strong style={{ fontSize: '0.85rem', color: 'var(--text-main)' }}>
                    {conversionPointsAttempted === 2 ? 'Intento de 2 Puntos (+2)' : 'Intento de 1 Punto (+1)'}
                  </strong>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    · Balón en Yarda {yardLineStart} ({50 - yardLineStart} yd a gol rival) · Posesión: España
                  </span>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setIsConversion(false)
                    setConversionPointsAttempted(null)
                    setConversionResult(null)
                    setConversionType(null)
                    setPrecedingTouchdownSnapId(null)
                    setCurrentDown(1)
                    setYardLineStart(5)
                  }}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'var(--text-muted)',
                    fontSize: '0.7rem',
                    cursor: 'pointer',
                    padding: '0.25rem 0.35rem',
                    textDecoration: 'underline',
                  }}
                  title="Cancelar intento de conversión y volver a serie ordinaria"
                >
                  Cancelar
                </button>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={() => setConversionResult('successful')}
                  style={{
                    flex: 1,
                    backgroundColor: conversionResult === 'successful' ? 'var(--status-success-bg)' : 'var(--surface-subtle)',
                    color: conversionResult === 'successful' ? 'var(--status-success-text)' : 'var(--text-main)',
                    border: conversionResult === 'successful' ? '1.5px solid var(--status-success-text)' : '1px solid var(--surface-border)',
                    fontSize: '0.8rem',
                    fontWeight: conversionResult === 'successful' ? 800 : 600,
                    padding: '0.45rem 0.75rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.35rem',
                  }}
                >
                  <span>✓ Conseguida (+{conversionPointsAttempted || 1})</span>
                </button>
                <button
                  type="button"
                  onClick={() => setConversionResult('failed')}
                  style={{
                    flex: 1,
                    backgroundColor: conversionResult === 'failed' ? 'rgba(239, 68, 68, 0.12)' : 'var(--surface-subtle)',
                    color: conversionResult === 'failed' ? '#dc2626' : 'var(--text-main)',
                    border: conversionResult === 'failed' ? '1.5px solid #dc2626' : '1px solid var(--surface-border)',
                    fontSize: '0.8rem',
                    fontWeight: conversionResult === 'failed' ? 800 : 600,
                    padding: '0.45rem 0.75rem',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.35rem',
                  }}
                >
                  <span>✗ Fallada (0)</span>
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
              <Flag size={14} color="var(--brand-red)" />
              <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
                Down y Posición de Campo (50 yd)
              </span>

              {/* Acción visible para recuperar el flujo de conversión en partido si se cerró o se registró previamente */}
              {capture?.mode === 'match' && !isConversion && (
                <button
                  type="button"
                  onClick={handleOpenConversionFlow}
                  style={{
                    backgroundColor: 'var(--surface-subtle)',
                    border: '1px solid var(--surface-border)',
                    borderRadius: '4px',
                    color: 'var(--brand-red)',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '0.2rem 0.5rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}
                  title="Abrir diálogo para registrar conversión de 1 o 2 puntos"
                >
                  <span>+ Registrar conversión</span>
                </button>
              )}
            </div>

            {/* Toggle de Práctica Guiada: Repetir Escenario */}
            {capture?.mode === 'guided_practice' && (
              <label style={{ fontSize: '0.68rem', display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', color: repeatScenario ? 'var(--brand-red)' : 'var(--text-muted)', fontWeight: 600 }}>
                <input
                  type="checkbox"
                  checked={repeatScenario}
                  onChange={(e) => setRepeatScenario(e.target.checked)}
                />
                <span>Repetir escenario (mantener)</span>
              </label>
            )}
          </div>

          {/* Fila de controles de Down y Situación */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.45rem', marginBottom: '0.5rem' }}>
            {/* Selector de Down 1..4 */}
            <div style={{ display: 'flex', borderRadius: '6px', border: '1px solid var(--surface-border)', overflow: 'hidden' }}>
              {[1, 2, 3, 4].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setCurrentDown(d)}
                  style={{
                    backgroundColor: currentDown === d ? 'var(--brand-red)' : 'var(--surface-subtle)',
                    color: currentDown === d ? '#fff' : 'var(--text-main)',
                    fontSize: '0.74rem',
                    fontWeight: currentDown === d ? 800 : 600,
                    padding: '0.3rem 0.6rem',
                    border: 'none',
                    cursor: 'pointer',
                    minWidth: '40px',
                    minHeight: '32px',
                  }}
                >
                  {d}º
                </button>
              ))}
            </div>

            {/* Botón rápido Primer Down */}
            <button
              type="button"
              onClick={() => {
                setIsFirstDown(!isFirstDown)
                if (!isFirstDown) setIsTurnover(false)
              }}
              style={{
                fontSize: '0.7rem',
                fontWeight: isFirstDown ? 800 : 600,
                padding: '0.3rem 0.55rem',
                borderRadius: '5px',
                border: isFirstDown ? '1.5px solid var(--status-success-text)' : '1px solid var(--surface-border)',
                backgroundColor: isFirstDown ? 'var(--status-success-bg)' : 'var(--surface-subtle)',
                color: isFirstDown ? 'var(--status-success-text)' : 'var(--text-main)',
                cursor: 'pointer',
                minHeight: '32px',
              }}
            >
              1st Down
            </button>

            {/* Botón rápido Turnover / Cambio de posesión */}
            <button
              type="button"
              onClick={() => {
                setIsTurnover(!isTurnover)
                if (!isTurnover) {
                  setIsFirstDown(false)
                  setIsTouchdown(false)
                }
              }}
              style={{
                fontSize: '0.7rem',
                fontWeight: isTurnover ? 800 : 600,
                padding: '0.3rem 0.55rem',
                borderRadius: '5px',
                border: isTurnover ? '1.5px solid var(--brand-red)' : '1px solid var(--surface-border)',
                backgroundColor: isTurnover ? 'var(--brand-red-subtle)' : 'var(--surface-subtle)',
                color: isTurnover ? 'var(--brand-red)' : 'var(--text-main)',
                cursor: 'pointer',
                minHeight: '32px',
              }}
            >
              Turnover
            </button>

            {/* Botón rápido Touchdown (Ajuste M4 - Requerimiento 1) */}
            <button
              type="button"
              onClick={() => {
                const nextVal = !isTouchdown
                setIsTouchdown(nextVal)
                if (nextVal) {
                  setIsFirstDown(false)
                  setIsTurnover(false)
                  setYardLineEnd(50)
                  if (!scorerAthleteId) {
                    if (receiverAthleteId) setScorerAthleteId(receiverAthleteId)
                    else if (passerAthleteId) setScorerAthleteId(passerAthleteId)
                  }
                } else {
                  setYardLineEnd(null)
                  setScorerAthleteId(null)
                  setHadLateral(false)
                }
              }}
              style={{
                fontSize: '0.7rem',
                fontWeight: isTouchdown ? 800 : 600,
                padding: '0.3rem 0.55rem',
                borderRadius: '5px',
                border: isTouchdown ? '1.5px solid var(--brand-red)' : '1px solid var(--surface-border)',
                backgroundColor: isTouchdown ? 'var(--brand-red)' : 'var(--surface-subtle)',
                color: isTouchdown ? '#fff' : 'var(--text-main)',
                cursor: 'pointer',
                minHeight: '32px',
                display: 'flex',
                alignItems: 'center',
                gap: '0.2rem',
              }}
            >
              Touchdown
            </button>
          </div>

          {/* Fila de Yarda Inicial, Yarda Final y Ganancia Neta */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.2fr', gap: '0.45rem', alignItems: 'center' }}>
            {/* Balón Inicial */}
            <div>
              <label className="form-label" style={{ fontSize: '0.66rem', marginBottom: '0.15rem' }}>
                Inicio: <strong>{formatFieldYard(yardLineStart)}</strong>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={yardLineStart}
                  onChange={(e) => setYardLineStart(Math.max(0, Math.min(50, Number(e.target.value))))}
                  className="form-input"
                  style={{ fontSize: '0.78rem', height: '32px', padding: '0.2rem 0.4rem' }}
                />
              </div>
            </div>

            {/* Balón Final */}
            <div>
              <label className="form-label" style={{ fontSize: '0.66rem', marginBottom: '0.15rem' }}>
                Fin: <strong>{isTouchdown ? '50 (Gol Rival)' : formatFieldYard(yardLineEnd)}</strong>
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                <input
                  type="number"
                  min={0}
                  max={50}
                  placeholder="Pendiente"
                  disabled={isTouchdown}
                  value={isTouchdown ? 50 : (yardLineEnd !== null ? yardLineEnd : '')}
                  onChange={(e) =>
                    setYardLineEnd(e.target.value === '' ? null : Math.max(0, Math.min(50, Number(e.target.value))))
                  }
                  className="form-input"
                  style={{
                    fontSize: '0.78rem',
                    height: '32px',
                    padding: '0.2rem 0.4rem',
                    backgroundColor: isTouchdown ? 'var(--brand-red-subtle)' : undefined,
                    fontWeight: isTouchdown ? 700 : 400,
                  }}
                />
              </div>
            </div>

            {/* Ganancia Neta y presets */}
            <div style={{ backgroundColor: 'var(--surface-subtle)', padding: '0.35rem 0.5rem', borderRadius: '6px', textAlign: 'center' }}>
              <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', display: 'block' }}>
                Ganancia neta
              </span>
              <strong
                style={{
                  fontSize: '0.92rem',
                  color: isTouchdown || (netYardsCurrent && netYardsCurrent > 0)
                    ? 'var(--status-success-text)'
                    : netYardsCurrent && netYardsCurrent < 0
                    ? 'var(--brand-red)'
                    : 'var(--text-main)',
                }}
              >
                {isTouchdown ? `+${50 - yardLineStart} yd` : formatNetYards(netYardsCurrent)}
              </strong>
            </div>
          </div>

          {/* Presets rápidos de campo */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.45rem', overflowX: 'auto' }}>
            <span style={{ fontSize: '0.65rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Presets:</span>
            {[
              { label: 'Propia 5', val: 5 },
              { label: 'Propia 10', val: 10 },
              { label: 'Propia 20', val: 20 },
              { label: 'Medio (25)', val: 25 },
              { label: 'Rival 10', val: 40 },
              { label: 'Rival 5', val: 45 },
            ].map((p) => (
              <button
                key={p.val}
                type="button"
                onClick={() => {
                  if (yardLineEnd === null) {
                    setYardLineEnd(p.val)
                  } else {
                    setYardLineStart(p.val)
                  }
                }}
                style={{
                  fontSize: '0.65rem',
                  padding: '0.15rem 0.35rem',
                  borderRadius: '4px',
                  border: '1px solid var(--surface-border)',
                  backgroundColor: 'var(--surface-card)',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Panel de Touchdown y Atribución (Ajuste M4 - Requerimiento 1) */}
          {isTouchdown && (
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.55rem 0.75rem',
                backgroundColor: 'rgba(217, 37, 53, 0.08)',
                border: '1.5px solid var(--brand-red)',
                borderRadius: '6px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
                <span style={{ fontWeight: 800, fontSize: '0.76rem', color: 'var(--brand-red)', textTransform: 'uppercase' }}>
                  Touchdown (+6) Registrado
                </span>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                  Línea final: <strong>50 (Línea de gol rival)</strong> · <strong>+{50 - yardLineStart} yd netas</strong>
                </span>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '0.5rem', alignItems: 'center' }}>
                <div>
                  <label className="form-label" style={{ fontSize: '0.66rem', marginBottom: '0.15rem' }}>
                    Anotador (participante real en campo):
                  </label>
                  <select
                    value={scorerAthleteId || ''}
                    onChange={(e) => setScorerAthleteId(e.target.value || null)}
                    className="form-input"
                    style={{ fontSize: '0.74rem', height: '30px', padding: '0.15rem 0.35rem' }}
                  >
                    <option value="">-- Seleccionar anotador --</option>
                    <optgroup label="Ataque en campo">
                      {activeOffenseSlots.map((slot) => {
                        const ath = rosterAthletes.find((a) => a.id === slot.athlete_id)
                        return (
                          <option key={slot.athlete_id} value={slot.athlete_id}>
                            {slot.position}: {ath?.full_name || slot.athlete_id} #{ath?.dorsal_number ?? ''}
                          </option>
                        )
                      })}
                    </optgroup>
                    <optgroup label="Defensa en campo (Pick-6 / Retorno)">
                      {activeDefenseSlots.map((slot) => {
                        const ath = rosterAthletes.find((a) => a.id === slot.athlete_id)
                        return (
                          <option key={slot.athlete_id} value={slot.athlete_id}>
                            {slot.position}: {ath?.full_name || slot.athlete_id} #{ath?.dorsal_number ?? ''}
                          </option>
                        )
                      })}
                    </optgroup>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600, marginTop: '0.6rem' }}>
                    <input
                      type="checkbox"
                      checked={hadLateral}
                      onChange={(e) => setHadLateral(e.target.checked)}
                      style={{ cursor: 'pointer' }}
                    />
                    Lateral / Cambio de portador
                  </label>
                </div>
              </div>

              <div style={{ marginTop: '0.35rem', fontSize: '0.68rem', color: hadLateral ? 'var(--brand-yellow)' : 'var(--text-secondary)' }}>
                {hadLateral ? (
                  <span>
                    ⚠️ <strong>Reparto manual pendiente:</strong> Al existir lateral o cambio de portador, las {50 - yardLineStart} yd no se atribuirán automáticamente al anotador. El desglose individual queda pendiente.
                  </span>
                ) : (
                  <span>
                    ✓ El avance completo ({50 - yardLineStart} yd netas) se atribuye al anotador ({scorerAthleteId ? (rosterAthletes.find((a) => a.id === scorerAthleteId)?.full_name || 'Seleccionado') : 'Receptor en pase o corredor'}).
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 3C. REPETICIÓN PREVISTA DEL GUION O LLAMADAS TÁCTICAS */}
        <div
          style={{
            backgroundColor: 'var(--surface-card)',
            border: '1px solid var(--surface-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '0.55rem 0.75rem',
            marginBottom: '0.6rem',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          {scriptData ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '0.35rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Layers size={13} color="var(--brand-red)" />
                <span
                  style={{
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: 'var(--text-secondary)',
                    textTransform: 'uppercase',
                  }}
                >
                  Repetición {capture?.current_rep_index !== undefined ? capture.current_rep_index + 1 : 1} de{' '}
                  {scriptData.reps?.length || 0}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <button
                  type="button"
                  onClick={handleConfirmPlannedCalls}
                  style={{
                    backgroundColor: 'var(--brand-red-subtle)',
                    color: 'var(--brand-red)',
                    border: '1px solid var(--brand-red)',
                    borderRadius: '4px',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    padding: '0.2rem 0.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                  }}
                  title="Confirmar que se ejecutaron las llamadas previstas"
                >
                  <Check size={12} />
                  <span>Confirmar previstas</span>
                </button>

                <button
                  type="button"
                  onClick={handleSkipPlannedRep}
                  style={{
                    background: 'none',
                    border: '1px solid var(--surface-border)',
                    borderRadius: '4px',
                    fontSize: '0.68rem',
                    color: 'var(--text-muted)',
                    padding: '0.2rem 0.45rem',
                    cursor: 'pointer',
                  }}
                  title="Saltar repetición sin crear snap"
                >
                  Saltar
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: '0.35rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Layers size={13} color="var(--brand-red)" />
              <span
                style={{
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: 'var(--text-secondary)',
                  textTransform: 'uppercase',
                }}
              >
                Llamadas Tácticas del Snap
              </span>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.45rem' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.15rem' }}>
                <label className="form-label" style={{ fontSize: '0.68rem', color: 'var(--brand-red)', margin: 0 }}>
                  Jugada Ataque {offenseCallPlanned ? `(Prev: ${offenseCallPlanned})` : ''}
                </label>
                {offenseCallActual && (
                  <button
                    type="button"
                    onClick={() => setOffenseCallActual('')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      fontSize: '0.68rem',
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                    title="Limpiar jugada seleccionada"
                  >
                    Borrar
                  </button>
                )}
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  list="offensive-plays-catalog"
                  value={offenseCallActual}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setOffenseCallActual(e.target.value)}
                  placeholder="Ej. Flood / Deep Comebacks"
                  className="form-input"
                  style={{
                    fontSize: '0.78rem',
                    height: '32px',
                    padding: '0.2rem 1.6rem 0.2rem 0.5rem',
                    width: '100%',
                  }}
                />
                {offenseCallActual && (
                  <button
                    type="button"
                    onClick={() => setOffenseCallActual('')}
                    style={{
                      position: 'absolute',
                      right: '6px',
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      fontSize: '1rem',
                      lineHeight: 1,
                      cursor: 'pointer',
                      padding: '2px',
                    }}
                    title="Limpiar texto"
                  >
                    ×
                  </button>
                )}
              </div>
              <datalist id="offensive-plays-catalog">
                {offensivePlays.map((p) => (
                  <option key={p.id} value={p.name} />
                ))}
              </datalist>

              {/* Selector directo de catálogo para ver todas las opciones sin teclear */}
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) setOffenseCallActual(e.target.value)
                }}
                className="form-input"
                style={{
                  fontSize: '0.7rem',
                  height: '24px',
                  padding: '0 0.35rem',
                  marginTop: '0.2rem',
                  color: 'var(--text-secondary)',
                  backgroundColor: 'var(--surface-subtle)',
                }}
                title="Elegir directamente del catálogo de jugadas autorizadas"
              >
                <option value="">-- Ver catálogo ({offensivePlays.length} jugadas) --</option>
                {offensivePlays.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name} {p.short_code ? `[${p.short_code}]` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.15rem' }}>
                <label className="form-label" style={{ fontSize: '0.68rem', color: 'var(--nav-bg-surface)', margin: 0 }}>
                  Llamada Defensa {defenseCallPlanned ? `(Prev: ${defenseCallPlanned})` : ''}
                </label>
                {defenseCallActual && (
                  <button
                    type="button"
                    onClick={() => setDefenseCallActual('')}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      fontSize: '0.68rem',
                      cursor: 'pointer',
                      padding: 0,
                      textDecoration: 'underline',
                    }}
                    title="Limpiar llamada seleccionada"
                  >
                    Borrar
                  </button>
                )}
              </div>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <input
                  type="text"
                  list="defensive-calls-catalog"
                  value={defenseCallActual}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => setDefenseCallActual(e.target.value)}
                  placeholder="Ej. Cover 2 Blitz / Bracket"
                  className="form-input"
                  style={{
                    fontSize: '0.78rem',
                    height: '32px',
                    padding: '0.2rem 1.6rem 0.2rem 0.5rem',
                    width: '100%',
                  }}
                />
                {defenseCallActual && (
                  <button
                    type="button"
                    onClick={() => setDefenseCallActual('')}
                    style={{
                      position: 'absolute',
                      right: '6px',
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      fontSize: '1rem',
                      lineHeight: 1,
                      cursor: 'pointer',
                      padding: '2px',
                    }}
                    title="Limpiar texto"
                  >
                    ×
                  </button>
                )}
              </div>
              <datalist id="defensive-calls-catalog">
                {defensiveCalls.map((c) => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>

              {/* Selector directo de catálogo para ver todas las opciones sin teclear */}
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) setDefenseCallActual(e.target.value)
                }}
                className="form-input"
                style={{
                  fontSize: '0.7rem',
                  height: '24px',
                  padding: '0 0.35rem',
                  marginTop: '0.2rem',
                  color: 'var(--text-secondary)',
                  backgroundColor: 'var(--surface-subtle)',
                }}
                title="Elegir directamente del catálogo de llamadas autorizadas"
              >
                <option value="">-- Ver catálogo ({defensiveCalls.length} llamadas) --</option>
                {defensiveCalls.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name} {c.short_code ? `[${c.short_code}]` : ''}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* 4. QUINTETOS ACTIVOS, SELECCIÓN DE JUGADOR Y SUSTITUCIÓN */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '0.75rem' }}>
          {/* QUINTETO DE ATAQUE (5 JUGADORES) */}
          {(unitView === 'both' || unitView === 'offense') && (
            <div
              style={{
                backgroundColor: 'var(--surface-card)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--surface-border)',
                padding: '0.6rem 0.75rem',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.45rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span
                    className="badge badge-red"
                    style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}
                  >
                    Ataque · {offenseLineupName}
                  </span>
                  {isOffenseCustom && (
                    <span
                      style={{
                        fontSize: '0.65rem',
                        color: 'var(--brand-yellow)',
                        fontWeight: 600,
                        backgroundColor: 'var(--brand-yellow-subtle)',
                        padding: '0.1rem 0.35rem',
                        borderRadius: '3px',
                      }}
                    >
                      Modificado
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <select
                    value={lineupTemplates.find((t) => t.name === offenseLineupName)?.id || ''}
                    onChange={(e) => handleSelectLineupTemplate('offense', e.target.value)}
                    style={{
                      fontSize: '0.7rem',
                      padding: '0.15rem 0.35rem',
                      borderRadius: '4px',
                      border: '1px solid var(--surface-border)',
                      backgroundColor: 'var(--surface-subtle)',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">Cambiar quinteto...</option>
                    {lineupTemplates
                      .filter((l) => l.unit === 'offense')
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => handleResetToTemplate('offense')}
                    style={{
                      fontSize: '0.68rem',
                      color: 'var(--brand-red)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                    title="Volver a la plantilla original de ataque"
                  >
                    Restablecer ataque
                  </button>
                </div>
              </div>

              {/* Botones de los 5 jugadores de ataque (Tap para acción rápida o sustituir) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.35rem' }}>
                {activeOffenseSlots.map((slot) => {
                  const isPasser = slot.athlete_id === passerAthleteId
                  const isReceiver = slot.athlete_id === receiverAthleteId
                  const isActorSelected = selectedActor?.athleteId === slot.athlete_id

                  return (
                    <div
                      key={slot.position}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          handleSelectAthleteForAction({
                            athleteId: slot.athlete_id,
                            name: slot.athlete_name || 'Atleta',
                            dorsal: slot.dorsal_number ?? null,
                            unit: 'offense',
                            position: slot.position,
                          })
                        }
                        style={{
                          width: '100%',
                          padding: '0.4rem 0.2rem',
                          borderRadius: '6px',
                          border: isActorSelected
                            ? '2.5px solid var(--brand-blue, #2563eb)'
                            : isPasser
                            ? '2px solid var(--brand-red)'
                            : isReceiver
                            ? '2px solid var(--status-success-text)'
                            : '1px solid var(--surface-border)',
                          backgroundColor: isActorSelected
                            ? 'rgba(37, 99, 235, 0.12)'
                            : isPasser
                            ? 'var(--surface-subtle)'
                            : isReceiver
                            ? 'var(--status-success-bg)'
                            : 'var(--surface-card)',
                          boxShadow: isActorSelected ? '0 0 0 2px rgba(37, 99, 235, 0.35)' : 'none',
                          textAlign: 'center',
                          cursor: 'pointer',
                          minHeight: '48px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.1s',
                        }}
                        title={`Pulsar para añadir acción rápida de ${slot.athlete_name}`}
                      >
                        <span
                          style={{
                            fontSize: '0.68rem',
                            fontWeight: 800,
                            color: isPasser ? 'var(--brand-red)' : 'var(--text-secondary)',
                          }}
                        >
                          {slot.position}
                          {isPasser && ' (QB)'}
                        </span>
                        <span
                          style={{
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            color: 'var(--text-main)',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {(slot.athlete_name || 'Atleta').split(' ')[0]}
                        </span>
                        <span
                          style={{
                            fontSize: '0.65rem',
                            color: 'var(--text-muted)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {slot.dorsal_number ? `#${slot.dorsal_number}` : ''}
                        </span>
                      </button>

                      {/* Botón flotante para sustitución */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenSubstitution('offense', slot)
                        }}
                        style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          backgroundColor: 'var(--surface-subtle)',
                          border: '1px solid var(--surface-border)',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                          padding: 0,
                        }}
                        title="Sustituir jugador"
                      >
                        <Edit2 size={9} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* QUINTETO DE DEFENSA (5 JUGADORES) */}
          {(unitView === 'both' || unitView === 'defense') && (
            <div
              style={{
                backgroundColor: 'var(--surface-card)',
                borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--surface-border)',
                padding: '0.6rem 0.75rem',
                boxShadow: 'var(--shadow-card)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.45rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span
                    className="badge badge-neutral"
                    style={{ fontSize: '0.7rem', textTransform: 'uppercase' }}
                  >
                    Defensa · {defenseLineupName}
                  </span>
                  {isDefenseCustom && (
                    <span
                      style={{
                        fontSize: '0.65rem',
                        color: 'var(--brand-yellow)',
                        fontWeight: 600,
                        backgroundColor: 'var(--brand-yellow-subtle)',
                        padding: '0.1rem 0.35rem',
                        borderRadius: '3px',
                      }}
                    >
                      Modificado
                    </span>
                  )}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <select
                    value={lineupTemplates.find((t) => t.name === defenseLineupName)?.id || ''}
                    onChange={(e) => handleSelectLineupTemplate('defense', e.target.value)}
                    style={{
                      fontSize: '0.7rem',
                      padding: '0.15rem 0.35rem',
                      borderRadius: '4px',
                      border: '1px solid var(--surface-border)',
                      backgroundColor: 'var(--surface-subtle)',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                    }}
                  >
                    <option value="">Cambiar quinteto...</option>
                    {lineupTemplates
                      .filter((l) => l.unit === 'defense')
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.name}
                        </option>
                      ))}
                  </select>

                  <button
                    type="button"
                    onClick={() => handleResetToTemplate('defense')}
                    style={{
                      fontSize: '0.68rem',
                      color: 'var(--brand-red)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                    title="Volver a la plantilla original de defensa"
                  >
                    Restablecer defensa
                  </button>
                </div>
              </div>

              {/* Botones de los 5 jugadores de defensa (Tap para acción rápida o sustituir) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.35rem' }}>
                {activeDefenseSlots.map((slot) => {
                  const isTackler = slot.athlete_id === tacklerAthleteId
                  const isActorSelected = selectedActor?.athleteId === slot.athlete_id

                  return (
                    <div
                      key={slot.position}
                      style={{
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          handleSelectAthleteForAction({
                            athleteId: slot.athlete_id,
                            name: slot.athlete_name || 'Atleta',
                            dorsal: slot.dorsal_number ?? null,
                            unit: 'defense',
                            position: slot.position,
                          })
                        }
                        style={{
                          width: '100%',
                          padding: '0.4rem 0.2rem',
                          borderRadius: '6px',
                          border: isActorSelected
                            ? '2.5px solid var(--brand-blue, #2563eb)'
                            : isTackler
                            ? '2px solid var(--nav-bg-surface)'
                            : '1px solid var(--surface-border)',
                          backgroundColor: isActorSelected
                            ? 'rgba(37, 99, 235, 0.12)'
                            : isTackler
                            ? 'var(--surface-subtle)'
                            : 'var(--surface-card)',
                          boxShadow: isActorSelected ? '0 0 0 2px rgba(37, 99, 235, 0.35)' : 'none',
                          textAlign: 'center',
                          cursor: 'pointer',
                          minHeight: '48px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.1s',
                        }}
                        title={`Pulsar para añadir acción rápida de ${slot.athlete_name}`}
                      >
                        <span style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--nav-bg-surface)' }}>
                          {slot.position}
                        </span>
                        <span
                          style={{
                            fontSize: '0.74rem',
                            fontWeight: 600,
                            color: 'var(--text-main)',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {(slot.athlete_name || 'Atleta').split(' ')[0]}
                        </span>
                        <span
                          style={{
                            fontSize: '0.65rem',
                            color: 'var(--text-muted)',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {slot.dorsal_number ? `#${slot.dorsal_number}` : ''}
                        </span>
                      </button>

                      {/* Botón flotante para sustitución */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenSubstitution('defense', slot)
                        }}
                        style={{
                          position: 'absolute',
                          top: '-4px',
                          right: '-4px',
                          backgroundColor: 'var(--surface-subtle)',
                          border: '1px solid var(--surface-border)',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                          padding: 0,
                        }}
                        title="Sustituir jugador"
                      >
                        <Edit2 size={9} />
                      </button>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* 5. RESULTADO PRINCIPAL DEL SNAP */}
        <div
          style={{
            backgroundColor: 'var(--surface-card)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--surface-border)',
            padding: '0.75rem',
            marginBottom: '0.75rem',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '0.5rem',
            }}
          >
            <span
              style={{
                fontSize: '0.76rem',
                fontWeight: 700,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
              }}
            >
              Resultado Principal de la Jugada
            </span>

            <button
              type="button"
              onClick={handleMarkSnapStart}
              style={{
                fontSize: '0.7rem',
                padding: '0.2rem 0.5rem',
                borderRadius: '4px',
                border: hasStartedExplicitly ? '1px solid var(--status-success-text)' : '1px solid var(--surface-border)',
                backgroundColor: hasStartedExplicitly ? 'var(--status-success-bg)' : 'var(--surface-subtle)',
                color: hasStartedExplicitly ? 'var(--status-success-text)' : 'var(--text-secondary)',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                fontVariantNumeric: 'tabular-nums',
              }}
              title="Registrar instante exacto del snap"
            >
              <Clock size={12} />
              <span>
                {hasStartedExplicitly
                  ? `Snap: ${new Date(snapStartedAt).toLocaleTimeString()}`
                  : 'Fijar inicio snap'}
              </span>
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.45rem' }}>
            {primaryResultButtons.map((btn) => {
              const isSelected = primaryResult === btn.id
              return (
                <button
                  key={btn.id}
                  type="button"
                  onClick={() => setPrimaryResult(isSelected ? null : btn.id)}
                  style={{
                    padding: '0.65rem 0.35rem',
                    borderRadius: '8px',
                    border: isSelected ? '2px solid var(--brand-red)' : '1px solid var(--surface-border)',
                    backgroundColor: isSelected ? 'var(--brand-red-subtle)' : btn.color,
                    color: isSelected ? 'var(--brand-red)' : 'var(--text-main)',
                    fontSize: '0.82rem',
                    fontWeight: isSelected ? 800 : 600,
                    cursor: 'pointer',
                    minHeight: '48px',
                    boxShadow: isSelected ? '0 2px 6px rgba(185, 28, 28, 0.2)' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    transition: 'all 0.1s',
                  }}
                >
                  {btn.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* 6. LISTA DE EVENTOS POR SNAP Y REGISTRO RÁPIDO (Requerimiento 1) */}
        <div
          style={{
            backgroundColor: 'var(--surface-card)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--surface-border)',
            padding: '0.75rem',
            marginBottom: '0.75rem',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.45rem' }}>
            <span
              style={{
                fontSize: '0.74rem',
                fontWeight: 700,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
              }}
            >
              Registro Rápido de Acciones por Jugador
            </span>

            {selectedActor ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  color: 'var(--brand-blue, #2563eb)',
                  backgroundColor: 'rgba(37, 99, 235, 0.12)',
                  border: '1px solid rgba(37, 99, 235, 0.3)',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '4px',
                }}
              >
                <span>Acción para: {selectedActor.name} ({selectedActor.position})</span>
                <button
                  type="button"
                  onClick={() => setSelectedActor(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--brand-blue, #2563eb)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    padding: 0,
                  }}
                  title="Deseleccionar jugador"
                >
                  <X size={12} />
                </button>
              </div>
            ) : (
              <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                1º Pulsa jugador arriba → 2º Pulsa acción abajo
              </span>
            )}
          </div>

          {/* Chips de acciones disponibles para el jugador seleccionado */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.65rem' }}>
            {ACTION_DEFINITIONS.filter(
              (a) => !selectedActor || a.unit === 'both' || a.unit === selectedActor.unit
            ).map((action) => (
              <button
                key={action.id}
                type="button"
                onClick={() => handleAddEvent(action.id)}
                style={{
                  fontSize: '0.74rem',
                  fontWeight: 600,
                  padding: '0.35rem 0.65rem',
                  borderRadius: '6px',
                  border: '1px solid var(--surface-border)',
                  backgroundColor: action.badgeColor,
                  color: 'var(--text-main)',
                  cursor: 'pointer',
                  minHeight: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                }}
              >
                <Plus size={12} />
                <span>{action.label}</span>
              </button>
            ))}
          </div>

          {/* LISTA DE ACCIONES INCORPORADAS EN ESTE SNAP */}
          <div>
            <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>
              Acciones registradas en este snap ({explicitEvents.length}):
            </span>

            {explicitEvents.length === 0 ? (
              <div style={{ fontSize: '0.74rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '0.25rem' }}>
                Ninguna acción secundaria añadida aún.
              </div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginTop: '0.35rem' }}>
                {explicitEvents.map((ev, idx) => {
                  const allSlots = [...activeOffenseSlots, ...activeDefenseSlots]
                  const actor = allSlots.find((s) => s.athlete_id === ev.actor_athlete_id)
                  const label = ACTION_DEFINITIONS.find((a) => a.id === ev.event_type)?.label || ev.event_type

                  return (
                    <span
                      key={ev.id || idx}
                      style={{
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        backgroundColor: 'var(--surface-subtle)',
                        border: '1px solid var(--surface-border)',
                        padding: '0.2rem 0.5rem',
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem',
                        color: 'var(--text-main)',
                      }}
                    >
                      <span>
                        {actor ? `${actor.athlete_name} (${actor.position})` : 'Jugador'}:{' '}
                        <strong>{label}</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveExplicitEvent(idx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--brand-red)',
                          cursor: 'pointer',
                          padding: '0 0.15rem',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                        title="Quitar acción"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* 7. NOTAS TÁCTICAS Y REVISIÓN EN VÍDEO */}
        <div
          style={{
            backgroundColor: 'var(--surface-card)',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--surface-border)',
            padding: '0.65rem 0.75rem',
            marginBottom: '0.75rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
              NOTAS / OBSERVACIÓN DEL COACH
            </span>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.35rem',
                fontSize: '0.72rem',
                fontWeight: 600,
                color: flaggedForReview ? 'var(--brand-red)' : 'var(--text-muted)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={flaggedForReview}
                onChange={(e) => setFlaggedForReview(e.target.checked)}
              />
              <span>Marcar para revisar en vídeo</span>
            </label>
          </div>

          <input
            type="text"
            placeholder="Nota breve (ej. buen timing en el slant, blitz tardío)..."
            value={snapNotes}
            onChange={(e) => setSnapNotes(e.target.value)}
            className="form-input"
            style={{ fontSize: '0.8rem', height: '34px' }}
          />
        </div>

        {/* 8. RESUMEN COMPACTO PRE-COMMIT: "VER QUÉ SE ESTÁ REGISTRANDO" (Requerimiento 2) */}
        <div
          style={{
            backgroundColor: 'var(--surface-subtle)',
            borderRadius: 'var(--radius-sm)',
            border: '1.5px solid var(--surface-border)',
            padding: '0.75rem',
            marginBottom: '1rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.35rem' }}>
            <span style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--text-main)', textTransform: 'uppercase' }}>
              Resumen en Vivo del Snap #{snapSequence}
            </span>

            {/* Distinción clara de estados: borrador / guardando / guardado / error */}
            <span
              className={`badge ${
                syncStatus === 'saving_local'
                  ? 'badge-warning'
                  : syncStatus === 'needs_attention'
                  ? 'badge-danger'
                  : primaryResult
                  ? 'badge-success'
                  : 'badge-muted'
              }`}
              style={{ fontSize: '0.68rem', padding: '0.15rem 0.45rem' }}
            >
              {syncStatus === 'saving_local'
                ? 'Guardando...'
                : syncStatus === 'needs_attention'
                ? 'Error al sincronizar'
                : primaryResult
                ? 'Borrador Listo'
                : 'Borrador en curso'}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.45rem', fontSize: '0.75rem' }}>
            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.66rem', display: 'block' }}>Resultado:</span>
              <strong>{primaryResult ? primaryResultButtons.find((b) => b.id === primaryResult)?.label : 'Sin resultado'}</strong>
            </div>

            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.66rem', display: 'block' }}>Down & Yardaje:</span>
              <strong>
                {currentDown}º Down · {formatFieldYard(yardLineStart)}
                {yardLineEnd !== null ? ` → ${formatFieldYard(yardLineEnd)} (${formatNetYards(netYardsCurrent)})` : ' (Ganancia pendiente)'}
              </strong>
            </div>

            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.66rem', display: 'block' }}>Lanzador / Receptor:</span>
              <span>
                {passerAthleteId ? activeOffenseSlots.find((s) => s.athlete_id === passerAthleteId)?.athlete_name : 'QB'}
                {receiverAthleteId ? ` → ${activeOffenseSlots.find((s) => s.athlete_id === receiverAthleteId)?.athlete_name}` : ''}
              </span>
            </div>

            <div>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.66rem', display: 'block' }}>Llamadas:</span>
              <span>
                {offenseCallActual || '(Ataque)'} vs {defenseCallActual || '(Defensa)'}
              </span>
            </div>
          </div>

          {explicitEvents.length > 0 && (
            <div style={{ marginTop: '0.4rem', borderTop: '1px solid var(--surface-border)', paddingTop: '0.35rem', fontSize: '0.72rem' }}>
              <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Acciones registradas ({explicitEvents.length}): </span>
              {explicitEvents.map((e, i) => {
                const p = [...activeOffenseSlots, ...activeDefenseSlots].find((s) => s.athlete_id === e.actor_athlete_id)
                const l = ACTION_DEFINITIONS.find((a) => a.id === e.event_type)?.label || e.event_type
                return (
                  <span key={i} style={{ marginRight: '0.5rem', color: 'var(--text-main)' }}>
                    [{p ? p.athlete_name : 'Atleta'}: {l}]
                  </span>
                )
              })}
            </div>
          )}
        </div>
      </main>

      <footer
        className="field-console-footer"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'var(--surface-card)',
          borderTop: '1px solid var(--surface-border)',
          padding: '0.6rem 0.85rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.5rem',
          zIndex: 1060,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.06)',
          maxWidth: '720px',
          margin: '0 auto',
        }}
      >
        {capture?.status === 'completed' ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              🔒 Sesión finalizada ({pastSnaps.length} snaps persistidos). Captura cerrada.
            </span>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setHistoryModalOpen(true)}
              style={{ fontSize: '0.8rem', padding: '0.45rem 0.9rem', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              <History size={14} />
              Revisar Jugadas Snap a Snap
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => handleUndoDraft()}
              style={{
                backgroundColor: 'var(--surface-subtle)',
                border: '1px solid var(--surface-border)',
                borderRadius: '8px',
                color: 'var(--text-secondary)',
                fontSize: '0.76rem',
                fontWeight: 600,
                padding: '0.5rem 0.75rem',
                minHeight: '46px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
              }}
              title="Borrar selecciones de este snap"
            >
              <RotateCcw size={14} />
              <span>Deshacer</span>
            </button>

            <button
              type="button"
              onClick={handleMarkTeachingRepetition}
              style={{
                backgroundColor: repetitionOfSnapId ? 'var(--brand-yellow-subtle)' : 'var(--surface-subtle)',
                border: repetitionOfSnapId ? '1.5px solid var(--brand-yellow)' : '1px solid var(--surface-border)',
                borderRadius: '8px',
                color: repetitionOfSnapId ? 'var(--text-main)' : 'var(--text-secondary)',
                fontSize: '0.76rem',
                fontWeight: 600,
                padding: '0.5rem 0.75rem',
                minHeight: '46px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.3rem',
              }}
              title="Marcar repetición docente del snap anterior"
            >
              <RefreshCw size={14} />
              <span>{repetitionOfSnapId ? 'Repetición vinculada' : 'Repetir'}</span>
            </button>

            <button
              type="button"
              disabled={syncStatus === 'saving_local'}
              onClick={handleCommitAndNextSnap}
              style={{
                flex: 1,
                backgroundColor: 'var(--brand-red)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.88rem',
                fontWeight: 700,
                padding: '0.55rem 1rem',
                minHeight: '48px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 8px rgba(185, 28, 28, 0.3)',
              }}
            >
              <span>Finalizar y Siguiente Snap</span>
              <ArrowRight size={18} />
            </button>
          </>
        )}
      </footer>

      {/* MODAL DE SUSTITUCIÓN RÁPIDA */}
      {substitutionModal.currentSlot && (
        <QuickSubstitutionModal
          isOpen={substitutionModal.isOpen}
          onClose={() => setSubstitutionModal({ ...substitutionModal, isOpen: false })}
          unit={substitutionModal.unit}
          slotPosition={substitutionModal.slotPosition}
          currentSlot={substitutionModal.currentSlot}
          allRosterAthletes={rosterAthletes}
          activeOffenseSlots={activeOffenseSlots}
          activeDefenseSlots={activeDefenseSlots}
          onConfirmSubstitution={handleConfirmSubstitution}
          onResetToTemplate={() => handleResetToTemplate(substitutionModal.unit)}
          isCustomLineup={substitutionModal.unit === 'offense' ? isOffenseCustom : isDefenseCustom}
        />
      )}

      {/* MODAL DE MARCA DE VÍDEO */}
      <VideoReferenceModal
        isOpen={videoModalOpen}
        onClose={() => setVideoModalOpen(false)}
        segments={capture?.video_segments || []}
        onUpdateSegments={async (newSegs) => {
          if (!capture) return
          setCapture({ ...capture, video_segments: newSegs })
          await fieldCaptureService.updateVideoSegments(capture.id, newSegs)
        }}
        currentSnapSequence={snapSequence}
        currentSnapStartedAt={snapStartedAt}
        blockStartedAt={capture?.started_at || new Date().toISOString()}
        activeSegmentId={videoSegmentId}
        currentVideoMark={videoTimestampMark}
        onSetSnapVideoMark={(segmentId, mark, offsetSec) => {
          setVideoSegmentId(segmentId)
          setVideoTimestampMark(mark)
          setVideoOffsetSeconds(offsetSec)
          setVideoModalOpen(false)
        }}
      />

      {/* MODAL DE HISTORIAL CRONOLÓGICO COMPLETO SNAP A SNAP (Ajuste M4 - Requerimiento 3) */}
      <SnapHistoryModal
        isOpen={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        capture={capture}
        snaps={pastSnaps}
        onSelectSnap={(snap) => {
          setSelectedPastSnap(snap)
          setPastSnapDetailOpen(true)
        }}
        rosterAthletes={rosterAthletes}
      />

      {/* MODAL DE DETALLE Y AUDITORÍA DE SNAP PASADO */}
      <PastSnapDetailModal
        isOpen={pastSnapDetailOpen}
        onClose={() => {
          setPastSnapDetailOpen(false)
          setSelectedPastSnap(null)
        }}
        snap={selectedPastSnap}
        onCorrectResult={handleCorrectPastSnapResult}
        onVoidSnap={handleVoidPastSnap}
        onUpdateSnapDetails={handleUpdatePastSnapDetails}
        rosterAthletes={rosterAthletes}
      />

      {/* MODAL POST-TOUCHDOWN (Ajuste M4 - Requerimiento 1) */}
      {postTdModalOpen && (
        <div
          className="mobile-viewport-overlay"
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(10, 19, 32, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            padding: '1rem',
          }}
        >
          <div
            className="mobile-viewport-sheet"
            style={{
              backgroundColor: 'var(--surface-card)',
              borderRadius: '12px',
              padding: '1.25rem',
              maxWidth: '460px',
              width: '100%',
              boxShadow: 'var(--shadow-card)',
              border: '1.5px solid var(--brand-red)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.65rem' }}>
              <div
                style={{
                  backgroundColor: 'var(--brand-red)',
                  color: '#fff',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 900,
                  fontSize: '0.9rem',
                }}
              >
                TD
              </div>
              <div>
                <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 800 }}>
                  ¡Touchdown Registrado (+6)!
                </h4>
                <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
                  Conversión para España · Marcador actual: {capture?.match_context?.our_score ?? 6} - {capture?.match_context?.rival_score ?? 0}
                </p>
              </div>
            </div>

            {capture?.mode === 'match' || Boolean(capture?.match_context) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.85rem' }}>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setIsConversion(true)
                    setConversionPointsAttempted(1)
                    setConversionResult('pending')
                    setConversionType('1_point')
                    setCurrentDown(1)
                    setYardLineStart(45) // Contrato deportivo: a 5 yd de gol rival
                    setYardLineEnd(null)
                    setIsFirstDown(false)
                    setIsTurnover(false)
                    setIsTouchdown(false)
                    setPostTdModalOpen(false)
                  }}
                  style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '0.65rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}
                >
                  <div style={{ backgroundColor: 'var(--brand-red-subtle)', color: 'var(--brand-red)', fontWeight: 800, padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.82rem' }}>
                    1 PT
                  </div>
                  <div>
                    <strong style={{ fontSize: '0.88rem' }}>Conversión de 1 punto</strong>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      Situar balón en Yarda 45 (a 5 yd de la línea de gol rival)
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    setIsConversion(true)
                    setConversionPointsAttempted(2)
                    setConversionResult('pending')
                    setConversionType('2_points')
                    setCurrentDown(1)
                    setYardLineStart(40) // Contrato deportivo: a 10 yd de gol rival
                    setYardLineEnd(null)
                    setIsFirstDown(false)
                    setIsTurnover(false)
                    setIsTouchdown(false)
                    setPostTdModalOpen(false)
                  }}
                  style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '0.65rem 0.85rem', display: 'flex', alignItems: 'center', gap: '0.6rem' }}
                >
                  <div style={{ backgroundColor: 'var(--brand-yellow-subtle)', color: 'var(--text-main)', fontWeight: 800, padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.82rem' }}>
                    2 PTS
                  </div>
                  <div>
                    <strong style={{ fontSize: '0.88rem' }}>Conversión de 2 puntos</strong>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      Situar balón en Yarda 40 (a 10 yd de la línea de gol rival)
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setIsConversion(false)
                    setConversionPointsAttempted(null)
                    setConversionResult(null)
                    setConversionType(null)
                    setPrecedingTouchdownSnapId(null)
                    setCurrentDown(1)
                    setYardLineStart(5) // Propia 5 para nueva posesión ordinaria del rival
                    setYardLineEnd(null)
                    setIsFirstDown(false)
                    setIsTurnover(false)
                    setIsTouchdown(false)
                    setPostTdModalOpen(false)
                  }}
                  style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '0.65rem 0.85rem' }}
                >
                  <div>
                    <strong style={{ fontSize: '0.85rem' }}>Continuar sin registrar conversión</strong>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      Cerrar serie y situar balón en Yarda 5 propia para nueva posesión
                    </div>
                  </div>
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.85rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    setCurrentDown(1)
                    setYardLineStart(5)
                    setPostTdModalOpen(false)
                  }}
                  style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '0.6rem 0.8rem' }}
                >
                  <div>
                    <strong style={{ fontSize: '0.85rem' }}>Reiniciar serie desde Yarda 5</strong>
                    <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.85)' }}>
                      Comenzar nuevo drive con 1º Down
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setCurrentDown(1)
                    setPostTdModalOpen(false)
                  }}
                  style={{ justifyContent: 'flex-start', textAlign: 'left', padding: '0.6rem 0.8rem' }}
                >
                  <div>
                    <strong style={{ fontSize: '0.85rem' }}>Mantener posición y ajustar manualmente</strong>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                      Elige down y yarda en la consola
                    </div>
                  </div>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MODAL DE FINALIZAR PARTIDO / BLOQUE (Ajuste M4 - Requerimiento 2) */}
      {finalizeModalOpen && (
        <div
          className="mobile-viewport-overlay"
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(10, 19, 32, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            padding: '1rem',
          }}
        >
          <div
            className="mobile-viewport-sheet"
            style={{
              backgroundColor: 'var(--surface-card)',
              borderRadius: '12px',
              padding: '1.25rem',
              maxWidth: '460px',
              width: '100%',
              boxShadow: 'var(--shadow-card)',
              border: '1px solid var(--surface-border)',
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 800 }}>
              {capture?.mode === 'match' ? '¿Finalizar Partido?' : '¿Finalizar Bloque de Campo?'}
            </h4>

            {/* Comprobación 1: Snap abierto */}
            {(primaryResult || hasStartedExplicitly || explicitEvents.length > 0 || yardLineEnd !== null || isTouchdown) ? (
              <div
                style={{
                  backgroundColor: 'var(--brand-yellow-subtle)',
                  color: 'var(--brand-yellow)',
                  padding: '0.65rem 0.8rem',
                  borderRadius: '6px',
                  fontSize: '0.78rem',
                  marginBottom: '1rem',
                  border: '1px solid var(--brand-yellow)',
                }}
              >
                <strong>⚠️ Hay un snap abierto (#{snapSequence}) con datos sin confirmar:</strong>
                <p style={{ margin: '0.25rem 0 0.5rem', color: 'var(--text-main)' }}>
                  Debes resolver o descartar explícitamente este snap antes de finalizar la sesión.
                </p>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      await handleCommitAndNextSnap()
                    }}
                    style={{ fontSize: '0.74rem', padding: '0.3rem 0.6rem' }}
                  >
                    Guardar snap abierto
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      handleUndoDraft()
                    }}
                    style={{ fontSize: '0.74rem', padding: '0.3rem 0.6rem' }}
                  >
                    Descartar borrador
                  </button>
                </div>
              </div>
            ) : (
              <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                Se registrará la hora de finalización (<strong>{new Date().toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</strong>).
                Se detendrá el reloj y se evitará seguir añadiendo snaps accidentalmente.
                El historial completo de <strong>{pastSnaps.length} snaps</strong> quedará guardado para consulta y auditoría.
              </p>
            )}

            {/* Comprobación 2: Escrituras pendientes */}
            {syncStatus === 'saving_local' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', color: 'var(--brand-yellow)', marginBottom: '0.75rem' }}>
                <RefreshCw size={14} className="spin" />
                Esperando confirmación del servidor para las escrituras en curso...
              </div>
            )}

            {/* Notas de finalización */}
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ fontSize: '0.7rem' }}>
                Notas finales del {capture?.mode === 'match' ? 'partido' : 'bloque'} (opcional):
              </label>
              <textarea
                value={finalizeNotes}
                onChange={(e) => setFinalizeNotes(e.target.value)}
                className="form-input"
                rows={2}
                placeholder="Observaciones finales, condiciones, conclusiones tácticas..."
                style={{ fontSize: '0.78rem' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setFinalizeModalOpen(false)}
                style={{ fontSize: '0.8rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                disabled={Boolean(primaryResult || hasStartedExplicitly || explicitEvents.length > 0 || yardLineEnd !== null || isTouchdown) || syncStatus === 'saving_local'}
                onClick={async () => {
                  try {
                    setSyncStatus('saving_local')
                    await fieldCaptureService.saveCaptureFinalizeLocally({
                      captureId,
                      teamId,
                      userId: user?.id || 'anonymous',
                      notes: finalizeNotes.trim() || undefined,
                    })
                    setCapture({
                      ...capture!,
                      status: 'completed',
                      ended_at: new Date().toISOString(),
                    })
                    setFinalizeModalOpen(false)
                    await refreshPendingCount()
                  } catch (err: unknown) {
                    setSyncStatus('needs_attention')
                    setErrorMsg(err instanceof Error ? err.message : 'Error al finalizar captura.')
                  }
                }}
                style={{ fontSize: '0.8rem', backgroundColor: 'var(--brand-red)' }}
              >
                Confirmar y Finalizar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE REAPERTURA AUDITADA (Ajuste M4 - Requerimiento 2) */}
      {reopenModalOpen && (
        <div
          className="mobile-viewport-overlay"
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(10, 19, 32, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            padding: '1rem',
          }}
        >
          <div
            className="mobile-viewport-sheet"
            style={{
              backgroundColor: 'var(--surface-card)',
              borderRadius: '12px',
              padding: '1.25rem',
              maxWidth: '440px',
              width: '100%',
              boxShadow: 'var(--shadow-card)',
              border: '1px solid var(--surface-border)',
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 800 }}>
              Reabrir {capture?.mode === 'match' ? 'Partido' : 'Bloque'} para continuar
            </h4>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Esta acción requiere autorización y quedará registrada en la auditoría con tu usuario y hora actual.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label className="form-label" style={{ fontSize: '0.7rem' }}>
                Motivo de la reapertura:
              </label>
              <input
                type="text"
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                className="form-input"
                placeholder="Ej: Prórroga reglamentaria / Snaps adicionales de ajuste"
                style={{ fontSize: '0.78rem' }}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setReopenModalOpen(false)}
                style={{ fontSize: '0.8rem' }}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={async () => {
                  try {
                    setSyncStatus('saving_local')
                    await fieldCaptureService.saveCaptureReopenLocally({
                      captureId,
                      teamId,
                      userId: user?.id || 'anonymous',
                      reason: reopenReason.trim() || undefined,
                    })
                    setCapture({
                      ...capture!,
                      status: 'active',
                      ended_at: null,
                    })
                    setReopenModalOpen(false)
                    await refreshPendingCount()
                  } catch (err: unknown) {
                    setSyncStatus('needs_attention')
                    setErrorMsg(err instanceof Error ? err.message : 'Error al reabrir sesión.')
                  }
                }}
                style={{ fontSize: '0.8rem' }}
              >
                Reabrir Sesión
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMACIÓN DE SALIDA SI HAY BORRADOR */}
      {exitPromptOpen && (
        <div
          className="mobile-viewport-overlay"
          role="dialog"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(10, 19, 32, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
            padding: '1rem',
          }}
        >
          <div
            className="mobile-viewport-sheet"
            style={{
              backgroundColor: 'var(--surface-card)',
              borderRadius: 'var(--radius-sm)',
              padding: '1.25rem',
              maxWidth: '380px',
              width: '100%',
              boxShadow: 'var(--shadow-card)',
            }}
          >
            <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', fontWeight: 700 }}>
              ¿Salir sin guardar este snap?
            </h4>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
              Tienes acciones o datos introducidos en el snap #{snapSequence} que se perderán al salir.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setExitPromptOpen(false)}
                style={{ fontSize: '0.8rem' }}
              >
                Continuar en consola
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={onClose}
                style={{ fontSize: '0.8rem', backgroundColor: 'var(--brand-red)' }}
              >
                Salir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE OPERACIONES OUTBOX Y ESTADO OFFLINE (M5 Requerimiento 3 y 4) */}
      <SyncOperationsModal
        isOpen={syncModalOpen}
        onClose={() => setSyncModalOpen(false)}
        captureId={captureId}
        teamId={teamId}
        userId={user?.id || ''}
        onSyncCompleted={async () => {
          await refreshPendingCount()
          try {
            const fresh = await fieldCaptureService.getUnifiedCaptureDetails({
              captureId,
              teamId,
              userId: user?.id || '',
            })
            setCapture(fresh.capture)
            setPastSnaps(fresh.snaps)
          } catch {
            // offline
          }
        }}
      />
    </div>
  )
}
